import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import TopBar from '../components/TopBar';
import Footer from '../components/Footer';
import BabyModal from '../components/BabyModal';
import OpenConfirmationModal from '../components/OpenConfirmationModal';
import CircularMenu from '../components/CircularMenu';
import QuickWizard from '../components/QuickWizard';
import RecipeCard from '../components/RecipeCard';
import RecipeModal from '../components/RecipeModal';
import useBabyPrompt from '../hooks/useBabyPrompt';
import MarketListWizard from '../components/MarketListWizard';
import SmartSearchModal from '../components/SmartSearchModal';
import { API_BASE_URL } from '../config';
import { hydrateRecipeForModal } from '../utils/recipeModalData';
import {
  trackRecommendationShown,
  trackRecommendationAccepted,
  trackRecommendationSkipped,
  trackDecisionWow,
  trackRecipeRejectedReason
} from '../utils/productAnalytics';
import { motion, AnimatePresence } from 'framer-motion';
import Swal from 'sweetalert2';
import { formatCurrency, formatDecimal, formatQuantity, calculateBatchValue } from '../utils/formatUtils';
import { getIngredientFamily } from '../constants/IngredientFamilies';
import { estimateSeedLinePrice } from '../shared/shoppingSeedPricing';

// Helper to detect dairy products
const isDairyProduct = (itemName) => {
  if (!itemName) return false;
  const lowerName = itemName.toLowerCase();
  // Sadece 'süt' kelimesini içeren ama 'yoğurt', 'peynir' gibi ürünlerin parçası olmayan durumları yakala
  // Veya doğrudan 'süt', 'çiğ süt', 'köy sütü' gibi ifadeleri ara
  return (lowerName === 'süt' || lowerName.includes('çiğ süt') || lowerName.includes('köy sütü') || (lowerName.includes('süt') && !lowerName.includes('yoğurt') && !lowerName.includes('peynir') && !lowerName.includes('tatlı')));
};

const formatDateForInput = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export default function MyKitchen({ isDarkMode, toggleDarkMode }) {
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [activeZone, setActiveZone] = useState('Fridge');
  const [activeSubZone, setActiveSubZone] = useState('Tümü');
  const location = useLocation();
  const [inventoryData, setInventoryData] = useState([]); // Zone-specific data
  const [allInventoryData, setAllInventoryData] = useState([]); // All inventory for search
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [hasChildren, setHasChildren] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterMode, setFilterMode] = useState('zone'); // 'zone' | 'expiring'
  const [wastedStats, setWastedStats] = useState({ totalWasted: 0, count: 0 });
  const API_BASE = API_BASE_URL;

  const { profile: babyProfile, showBabyModal, loading: babyLoading, submitBabyData, closeBabyModal, refreshProfile } = useBabyPrompt();
  const [localBabyModalOpen, setLocalBabyModalOpen] = useState(false);
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [isMarketWizardOpen, setIsMarketWizardOpen] = useState(false);

  // Smart Search State
  const [smartSearchModalOpen, setSmartSearchModalOpen] = useState(false);
  const [smartSearchRecipes, setSmartSearchRecipes] = useState([]);
  const [smartStructuredMenu, setSmartStructuredMenu] = useState(null); // V21.0
  const [smartSearchLoading, setSmartSearchLoading] = useState(false);
  const [selectedSmartRecipe, setSelectedSmartRecipe] = useState(null);
  const [smartRecipeModalOpen, setSmartRecipeModalOpen] = useState(false);
  const [availableComponents, setAvailableComponents] = useState([]);
  const [smartPortion, setSmartPortion] = useState(4); // V21.0
  const [smartSkillLevel, setSmartSkillLevel] = useState('Acemi'); // V21.0

  const needsBabyPrompt = !babyLoading && showBabyModal && !hasChildren;

  const triggerBabyPrompt = () => {
    if (needsBabyPrompt) setLocalBabyModalOpen(true);
  };

  const getAuthHeaders = (extra = {}) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      return { Authorization: `Bearer ${token}`, ...extra };
    }
    return { ...extra };
  };

  const createIdempotencyKey = (prefix = 'manual_item') => {
    try {
      if (window?.crypto?.randomUUID) {
        return `${prefix}_${window.crypto.randomUUID()}`;
      }
    } catch (_) {
      // no-op
    }
    return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  };

  const normalizeFavoriteNameKey = (value = '') =>
    String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 120);

  const getInventoryFavoriteEntityId = (item) => {
    const dictionaryId = item?.ingredient?.id || item?.ingredient?.dictionaryId || item?.dictionaryId || item?.ingredient_id;
    if (dictionaryId !== undefined && dictionaryId !== null && String(dictionaryId).trim()) {
      return String(dictionaryId);
    }
    const nameKey = normalizeFavoriteNameKey(item?.ingredient_name || item?.ingredient?.name || '');
    return nameKey ? `name:${nameKey}` : null;
  };

  const getRecipeBehaviorEntityId = (recipe) => {
    const rawId = recipe?.idRaw ?? recipe?.id ?? null;
    if (rawId !== undefined && rawId !== null && String(rawId).trim()) {
      return String(rawId);
    }
    const nameKey = normalizeFavoriteNameKey(recipe?.name || recipe?.title || '');
    return nameKey ? `name:${nameKey}` : null;
  };

  const buildRecommendationId = (snapshotId, recipeId, rank) => {
    const sid = Number(snapshotId || 0);
    const rid = String(recipeId || 'unknown').replace(/[^a-zA-Z0-9_-]/g, '_');
    const rnk = Number(rank || 0);
    return `rec_${sid}_${rid}_${rnk}`.slice(0, 120);
  };

  const getDecisionChoiceMeta = (recipeId) => {
    const choices = Array.isArray(behaviorDecisionSet?.choices) ? behaviorDecisionSet.choices : [];
    const idx = choices.findIndex((x) => String(x?.recipeId || '') === String(recipeId || ''));
    if (idx < 0) return null;
    const choice = choices[idx] || {};
    const rank = idx + 1;
    return {
      rank,
      candidateCount: choices.length,
      suggestionType: rank === 1 ? 'primary' : 'secondary',
      recommendationId: buildRecommendationId(behaviorDecisionSet?.snapshotId, choice.recipeId, rank)
    };
  };

  const emitDecisionSetShownEvents = (decisionSet) => {
    const choices = Array.isArray(decisionSet?.choices) ? decisionSet.choices.slice(0, 3) : [];
    if (!choices.length) return;
    const snapshotId = decisionSet?.snapshotId || 0;
    const candidateCount = choices.length;

    choices.forEach((choice, idx) => {
      const recipeId = String(choice?.recipeId || '');
      if (!recipeId) return;
      const rank = idx + 1;
      const suggestionType = rank === 1 ? 'primary' : 'secondary';
      const recommendationId = buildRecommendationId(snapshotId, recipeId, rank);
      if (shownRecommendationRef.current.has(recommendationId)) return;

      shownRecommendationRef.current.add(recommendationId);
      shownAtByRecommendationRef.current.set(recommendationId, Date.now());
      trackRecommendationShown({
        source: 'kitchen_behavior_engine',
        surface: 'my_kitchen',
        context: 'decision_set',
        metadata: {
          recommendationId,
          recipeId,
          rank,
          candidateCount,
          suggestionType,
          snapshotId
        }
      });
    });
  };

  const fetchBehaviorDecisionSet = async () => {
    try {
      const resp = await fetch(`${API_BASE_URL}/api/kitchen/behavior/decision-set?windowDays=30`, {
        credentials: 'include',
        headers: getAuthHeaders({ Accept: 'application/json' })
      });
      if (!resp.ok) return;
      const json = await resp.json().catch(() => ({}));
      const nextDecisionSet = json?.decisionSet || null;
      setBehaviorDecisionSet(nextDecisionSet);
      emitDecisionSetShownEvents(nextDecisionSet);
    } catch (_) {
      // silent fallback
    }
  };

  const postDecisionFeedback = async (stage, recipeId) => {
    if (!recipeId) return;
    const targetRef = stage === 'accepted' ? acceptedFeedbackSentRef : completedFeedbackSentRef;
    if (targetRef.current.has(recipeId)) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/kitchen/behavior/decision-feedback`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders({
          Accept: 'application/json',
          'Content-Type': 'application/json'
        }),
        body: JSON.stringify({
          stage,
          recipeId,
          snapshotId: behaviorDecisionSet?.snapshotId || null
        })
      });
      if (response.ok) {
        targetRef.current.add(recipeId);
      }
    } catch (_) {
      // silent fallback
    }
  };

  const isInventoryFavorite = (item) => {
    const entityId = getInventoryFavoriteEntityId(item);
    if (!entityId) return false;
    return inventoryFavoriteIds.has(entityId);
  };

  const fetchInventoryFavorites = async () => {
    try {
      const resp = await fetch(`${API_BASE_URL}/api/favorites?entityType=inventory_item&mode=favorites&limit=500`, {
        credentials: 'include',
        headers: getAuthHeaders({ Accept: 'application/json' })
      });
      if (!resp.ok) return;
      const json = await resp.json().catch(() => ({}));
      const items = Array.isArray(json?.items) ? json.items : [];
      const ids = new Set(
        items
          .filter((x) => Boolean(x?.signals?.liked) || Boolean(x?.signals?.pinned))
          .map((x) => String(x.entityId || ''))
          .filter(Boolean)
      );
      setInventoryFavoriteIds(ids);
    } catch (error) {
      console.warn('Inventory favorites fetch failed', error);
    }
  };

  const toggleInventoryFavorite = async (item) => {
    const entityId = getInventoryFavoriteEntityId(item);
    if (!entityId) return;
    try {
      setFavoriteBusyEntityId(entityId);
      setFavoriteError('');
      const isFav = inventoryFavoriteIds.has(entityId);
      const method = isFav ? 'DELETE' : 'POST';
      const resp = await fetch(`${API_BASE_URL}/api/favorites`, {
        method,
        credentials: 'include',
        headers: getAuthHeaders({
          Accept: 'application/json',
          'Content-Type': 'application/json'
        }),
        body: JSON.stringify({
          entityType: 'inventory_item',
          entityId,
          type: 'liked',
          metadata: {
            ingredientName: item?.ingredient_name || item?.ingredient?.name || null
          }
        })
      });
      if (!resp.ok) {
        const j = await resp.json().catch(() => ({}));
        throw new Error(j?.error || 'favorite_toggle_failed');
      }
      setInventoryFavoriteIds((prev) => {
        const next = new Set(prev);
        if (isFav) next.delete(entityId);
        else next.add(entityId);
        return next;
      });
    } catch (error) {
      console.error('Inventory favorite toggle failed', error);
      setFavoriteError('favorite_toggle_failed');
    } finally {
      setFavoriteBusyEntityId('');
    }
  };

  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [modalMode, setModalMode] = useState('edit'); // 'edit' veya 'add'
  const [editForm, setEditForm] = useState({
    remaining_amount: '',
    unit: '',
    expiry_date: '',
    purchase_price: '',
    storage_location: '',
    main_zone: '',
    sub_zone: '',
    ingredient_name: '', // Yeni alan eklendi
    _default_price: 0,
    _default_price_unit: '',
    _unit_price: 0,
    _auto_price: false,
    _price_locked: false,
    _zone_locked: false
  });

  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingItem, setDeletingItem] = useState(null);

  // Open confirmation modal state
  const [openConfirmItem, setOpenConfirmItem] = useState(null);

  // Waste history modal state
  const [isWasteHistoryOpen, setIsWasteHistoryOpen] = useState(false);
  const [wasteHistory, setWasteHistory] = useState([]);
  const [wasteHistoryLoading, setWasteHistoryLoading] = useState(false);
  const [isValueModalOpen, setIsValueModalOpen] = useState(false);
  const [isStapleModalOpen, setIsStapleModalOpen] = useState(false);
  const [stapleList, setStapleList] = useState([]);
  const [stapleLoading, setStapleLoading] = useState(false);
  const [ontologyResults, setOntologyResults] = useState([]);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [reviewQueue, setReviewQueue] = useState([]);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewTrigger, setReviewTrigger] = useState(null);
  const [inventoryFavoriteIds, setInventoryFavoriteIds] = useState(new Set());
  const [favoriteBusyEntityId, setFavoriteBusyEntityId] = useState('');
  const [favoriteError, setFavoriteError] = useState('');
  const [behaviorDecisionSet, setBehaviorDecisionSet] = useState(null);
  const acceptedFeedbackSentRef = useRef(new Set());
  const completedFeedbackSentRef = useRef(new Set());
  const shownRecommendationRef = useRef(new Set());
  const shownAtByRecommendationRef = useRef(new Map());
  const skippedRecommendationRef = useRef(new Set());

  // Son kullanma tarihi hesaplama fonksiyonu
  const calculateExpiryDate = async (ingredientName) => {
    try {
      const response = await fetch(`${API_BASE}/api/inventory/ingredient-expiry?name=${encodeURIComponent(ingredientName)}`, {
        credentials: 'include',
        headers: getAuthHeaders({ Accept: 'application/json' })
      });

      if (response.ok) {
        const data = await response.json();
        return data.expiry_date;
      }
    } catch (error) {
      console.log('Son kullanma tarihi hesaplanamadı:', error);
    }

    // Varsayılan: Bugünden 7 gün sonra
    const defaultDate = new Date();
    defaultDate.setDate(defaultDate.getDate() + 7);
    return defaultDate.toISOString().split('T')[0];
  };

  // Zone ve subzone tanımları - Türkçe database değerleri
  const zones = {
    'Fridge': {
      name: 'Buzdolabı',
      icon: 'kitchen',
      subZones: ['Tümü', 'Sebzeler', 'Meyveler', 'Süt Ürünleri', 'Et & Balık', 'Soslar', 'Genel']
    },
    'Freezer': {
      name: 'Dondurucu',
      icon: 'ac_unit',
      subZones: ['Tümü', 'Donmuş Et', 'Donmuş Sebze', 'Tatlılar', 'Genel']
    },
    'Pantry': {
      name: 'Kiler',
      icon: 'inventory',
      subZones: ['Tümü', 'Tahıllar', 'Baharatlar', 'Konserveler', 'Genel']
    },
    'Snack_Cabinet': {
      name: 'Atıştırmalık\nDolabı',
      icon: 'cookie',
      subZones: ['Tümü', 'Atıştırmalıklar', 'İçecekler', 'Fırın Malzemeleri', 'Genel']
    }
  };

  // Zone mapping: Türkçe UI -> English DB Keys
  const zoneMapping = {
    'Buzdolabı': 'Fridge',
    'Dondurucu': 'Freezer',
    'Kiler': 'Pantry',
    'Atıştırmalık Dolabı': 'Snack_Cabinet'
  };

  // SubZone mapping: Türkçe UI -> English DB Keys
  const subZoneMapping = {
    'Tümü': '',
    // Buzdolabı
    'Sebzeler': 'Vegetables',
    'Meyveler': 'Fruits',
    'Süt Ürünleri': 'Dairy',
    'Et & Balık': 'Meat_Fish',
    'Soslar': 'Sauces',
    // Dondurucu
    'Donmuş Et': 'Frozen_Meat',
    'Donmuş Sebze': 'Frozen_Vegetables',
    'Tatlılar': 'Desserts',
    // Kiler
    'Tahıllar': 'Grains',
    'Baharatlar': 'Spices',
    'Konserveler': 'Canned',
    'Genel': 'General',
    // Atıştırmalık
    'Atıştırmalıklar': 'Snacks',
    'İçecekler': 'Beverages',
    'Fırın Malzemeleri': 'Baking'
  };



  // Listen for global profile updates
  useEffect(() => {
    const handleProfileUpdate = () => {
      console.log('🔄 Global profile update detected in MyKitchen, refreshing...');
      refreshProfile();
    };

    window.addEventListener('profileUpdated', handleProfileUpdate);
    return () => window.removeEventListener('profileUpdated', handleProfileUpdate);
  }, [refreshProfile]);

  useEffect(() => {
    fetchBehaviorDecisionSet();
  }, []);

  // Sync state with profile hook - HARDENED V22.12
  useEffect(() => {
    if (babyProfile) {
      console.log('👤 Profile updated in MyKitchen:', {
        hasBaby: babyProfile.hasBaby,
        allergies: babyProfile.allergies,
        babyName: babyProfile.babyName,
        babyBirthDate: babyProfile.babyBirthDate
      });

      // 🔄 Robust baby detection (supports multiple naming conventions)
      const hasBabyFlag = Boolean(
        babyProfile.hasBaby || 
        babyProfile.bebek_var_mi || 
        babyProfile.intakeSurvey?.answers?.bebek_var_mi === 'Evet' ||
        babyProfile.babyName ||
        babyProfile.babyBirthDate
      );

      // 📅 Calculate Age in Months (Crucial for SS-06 Baby Safety Rules)
      let ageMonths = 12; // Default if baby exists but birthdate unknown
      if (babyProfile.babyBirthDate) {
        const birth = new Date(babyProfile.babyBirthDate);
        const now = new Date();
        const diffMonths = (now.getFullYear() - birth.getFullYear()) * 12 + (now.getMonth() - birth.getMonth());
        ageMonths = Math.max(0, diffMonths);
      } else if (babyProfile.babyAgeGroup) {
        // Fallback to age group mapping
        const group = String(babyProfile.babyAgeGroup);
        if (group.includes('0-6')) ageMonths = 4;
        else if (group.includes('6-12')) ageMonths = 9;
        else if (group.includes('12-24')) ageMonths = 18;
      }

      const enhancedProfile = {
        ...babyProfile,
        hasBaby: hasBabyFlag,
        baby_age_months: ageMonths
      };

      setUserProfile(enhancedProfile);
      setHasChildren(hasBabyFlag);
      
      console.log('👶 Hardened Baby Status:', { hasBabyFlag, ageMonths });
    } else if (!babyLoading) {
      setHasChildren(false);
    }
  }, [babyProfile, babyLoading]);

  // İlk yüklemede TÜM inventory'yi çek (search için)
  useEffect(() => {
    fetchAllInventoryData();
    fetchInventoryFavorites();
    fetchWastedStats();
    fetchConsumptionReviewQueue();
  }, []);

  useEffect(() => {
    if (!allInventoryData || allInventoryData.length === 0) return;
    fetchInventoryFavorites();
  }, [allInventoryData.length]);

  // Listen for navigation state (e.g., from TopBar "Rescued" link)
  useEffect(() => {
    if (location.state?.filter) {
      console.log('🔗 Incoming filter detected:', location.state.filter);

      if (location.state.filter === 'wasted') {
        openWasteHistory();
      } else {
        setFilterMode(location.state.filter);
      }

      // Optional: Clear the state after processing to prevent sticky filter on manual refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Zone değiştiğinde zone-specific data çek ve subzone'u güncelle
  useEffect(() => {
    fetchInventoryData();
  }, [activeZone, activeSubZone]);

  // Zone değiştiğinde ilk subzone'u otomatik seç (sadece zone değişiminde)
  useEffect(() => {
    const currentZone = zones[activeZone];
    if (currentZone && currentZone.subZones && currentZone.subZones.length > 0) {
      const expectedSubZone = currentZone.subZones[0];
      console.log('🔄 Zone changed, auto-updating subzone:', { activeZone, newSubZone: expectedSubZone });
      setActiveSubZone(expectedSubZone);
    }
  }, [activeZone]); // Sadece activeZone değişince çalışır

  // Tüm inventory'yi çek (search için)
  const fetchAllInventoryData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(
        `${API_BASE}/api/inventory`,
        { credentials: 'include', headers: getAuthHeaders({ Accept: 'application/json' }) }
      );

      if (!response.ok) throw new Error('Failed to fetch data');

      const data = await response.json();
      console.log('📦 All inventory response:', data);
      setAllInventoryData(data.inventory || []);

    } catch (err) {
      console.error('All inventory fetch error:', err);
      setError('Veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };
  // Zone-specific inventory çek
  const fetchInventoryData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Türkçe UI değerlerini İngilizce database değerlerine çevir
      let mappedZone = activeZone;
      let mappedSubZone = activeSubZone;

      // Use centralized mappings
      mappedZone = zoneMapping[activeZone] || activeZone;
      mappedSubZone = subZoneMapping[activeSubZone] || activeSubZone;

      console.log('🔍 Fetching inventory:', {
        activeZone, activeSubZone,
        mappedZone, mappedSubZone
      });

      let url = `${API_BASE}/api/inventory?zone=${mappedZone}`;
      if (mappedSubZone) {
        url += `&subZone=${encodeURIComponent(mappedSubZone)}`;
      }

      const response = await fetch(url, { credentials: 'include', headers: getAuthHeaders({ Accept: 'application/json' }) });


      if (!response.ok) throw new Error('Failed to fetch data');

      const data = await response.json();
      console.log('📦 Inventory response:', data);
      setInventoryData(data.inventory || []);

    } catch (err) {
      console.error('Inventory fetch error:', err);
      setError('Veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const fetchWastedStats = async () => {
    try {
      const resp = await fetch(`${API_BASE}/api/inventory/wasted-stats`, {
        credentials: 'include',
        headers: getAuthHeaders({ Accept: 'application/json' })
      });
      if (resp.ok) {
        const data = await resp.json();
        setWastedStats(data);
      }
    } catch (err) {
      console.error('Wasted stats fetch error:', err);
    }
  };

  const fetchConsumptionReviewQueue = async () => {
    try {
      setReviewLoading(true);
      const resp = await fetch(`${API_BASE}/api/consumptions/review-queue?limit=12`, {
        credentials: 'include',
        headers: getAuthHeaders({ Accept: 'application/json' })
      });
      if (!resp.ok) {
        setReviewQueue([]);
        return;
      }
      const data = await resp.json();
      setReviewQueue(Array.isArray(data?.items) ? data.items : []);
      setReviewTrigger(data?.trigger || null);
    } catch (err) {
      console.error('Review queue fetch error:', err);
      setReviewQueue([]);
      setReviewTrigger(null);
    } finally {
      setReviewLoading(false);
    }
  };

  const markReviewPromptShown = async () => {
    try {
      await fetch(`${API_BASE}/api/consumptions/review-trigger/mark-shown`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' })
      });
    } catch (err) {
      console.warn('markReviewPromptShown failed:', err?.message || err);
    }
  };

  const submitManualConsumption = async (item, { amount, unit }) => {
    const key = createIdempotencyKey(`manual_item_${item?.id || 'unknown'}`);
    const resp = await fetch(`${API_BASE}/api/consumptions/manual-item`, {
      method: 'POST',
      credentials: 'include',
      headers: getAuthHeaders({
        'Content-Type': 'application/json',
        'X-Idempotency-Key': key
      }),
      body: JSON.stringify({
        inventoryItemId: item.id,
        requestedAmount: Number(amount),
        requestedUnit: String(unit),
        idempotencyKey: key,
        source: 'manual_item_quick_entry',
        metadata: {
          ingredientName: item.ingredient_name || item.ingredient?.name || null
        }
      })
    });
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      throw new Error(err?.error || 'Manual consumption failed');
    }
    return resp.json();
  };

  const handleQuickManualConsume = async (item) => {
    const defaultUnit = String(item?.unit || 'adet');
    const defaultAmount = 1;
    const swalTheme = isDarkMode
      ? {
          background: '#0f172a',
          color: '#e5e7eb',
          confirmButtonColor: '#dc2626',
          cancelButtonColor: '#334155'
        }
      : {
          background: '#ffffff',
          color: '#0f172a',
          confirmButtonColor: '#dc2626',
          cancelButtonColor: '#64748b'
        };
    const result = await Swal.fire({
      title: 'Bunu Yedim',
      html: `
        <div class="space-y-3 text-left">
          <label class="block text-xs text-slate-500">Miktar</label>
          <input id="consume-amount" type="number" min="0.1" step="0.1" value="${defaultAmount}" class="swal2-input" style="margin:0;width:100%" />
          <label class="block text-xs text-slate-500 mt-2">Birim</label>
          <select id="consume-unit" class="swal2-input" style="margin:0;width:100%">
            ${[defaultUnit, 'adet', 'gr', 'kg', 'ml', 'lt'].filter((v, i, arr) => arr.indexOf(v) === i).map((u) => `<option value="${u}">${u}</option>`).join('')}
          </select>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Review Kuyruğuna Ekle',
      cancelButtonText: 'Vazgeç',
      ...swalTheme,
      preConfirm: () => {
        const amount = Number(document.getElementById('consume-amount')?.value || 0);
        const unit = String(document.getElementById('consume-unit')?.value || '').trim();
        if (!Number.isFinite(amount) || amount <= 0) {
          Swal.showValidationMessage('Geçerli bir miktar giriniz.');
          return false;
        }
        if (!unit) {
          Swal.showValidationMessage('Birim seçiniz.');
          return false;
        }
        return { amount, unit };
      }
    });

    if (!result.isConfirmed || !result.value) return;
    try {
      await submitManualConsumption(item, result.value);
      await fetchConsumptionReviewQueue();
      Swal.fire({
        icon: 'success',
        title: 'Eklendi',
        text: `${item.ingredient_name || 'Ürün'} review kuyruğuna eklendi.`,
        ...swalTheme,
        timer: 1500,
        showConfirmButton: false
      });
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Hata',
        text: err.message || 'İşlem başarısız',
        ...swalTheme
      });
    }
  };

  const reviewAction = async (id, action, payload = {}) => {
    const resp = await fetch(`${API_BASE}/api/consumptions/${id}/${action}`, {
      method: 'POST',
      credentials: 'include',
      headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(payload)
    });
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      throw new Error(err?.error || 'Review action failed');
    }
    return resp.json();
  };

  const handleReviewApprove = async (item) => {
    try {
      await reviewAction(item.id, 'review-approve', {
        requestedAmount: item.requestedAmount,
        requestedUnit: item.requestedUnit
      });
      await Promise.all([fetchConsumptionReviewQueue(), fetchInventoryData(), fetchAllInventoryData()]);
    } catch (err) {
      Swal.fire('Hata', err.message || 'Onay işlemi başarısız', 'error');
    }
  };

  const handleReviewReject = async (item) => {
    try {
      await reviewAction(item.id, 'review-reject');
      await fetchConsumptionReviewQueue();
    } catch (err) {
      Swal.fire('Hata', err.message || 'Reddetme işlemi başarısız', 'error');
    }
  };

  const setManualItemPreference = async (item, payload = {}) => {
    const resp = await fetch(`${API_BASE}/api/consumptions/preferences/manual-item`, {
      method: 'POST',
      credentials: 'include',
      headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({
        inventoryItemId: item.inventoryItemId,
        ...payload
      })
    });
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      throw new Error(err?.error || 'Preference save failed');
    }
    return resp.json();
  };

  const handleSetAutoApply = async (item) => {
    try {
      await setManualItemPreference(item, { autoApplyPreference: 'auto' });
      Swal.fire({
        icon: 'success',
        title: 'Kaydedildi',
        text: 'Bu ürün için "Her zaman otomatik düş" aktif edildi.',
        timer: 1500,
        showConfirmButton: false
      });
      await fetchConsumptionReviewQueue();
    } catch (err) {
      Swal.fire('Hata', err.message || 'Tercih kaydedilemedi', 'error');
    }
  };

  const handleMuteSuggestion = async (item) => {
    try {
      await reviewAction(item.id, 'mute-suggestion');
      await fetchConsumptionReviewQueue();
    } catch (err) {
      Swal.fire('Hata', err.message || 'Mute işlemi başarısız', 'error');
    }
  };

  useEffect(() => {
    if (reviewTrigger?.shouldPrompt) {
      markReviewPromptShown();
    }
  }, [reviewTrigger?.shouldPrompt]);

  const fetchWasteHistory = async () => {
    try {
      setWasteHistoryLoading(true);
      const resp = await fetch(`${API_BASE}/api/inventory/wasted-history`, {
        credentials: 'include',
        headers: getAuthHeaders({ Accept: 'application/json' })
      });
      if (resp.ok) {
        const data = await resp.json();
        setWasteHistory(data.history || []);
      }
    } catch (err) {
      console.error('Waste history fetch error:', err);
    } finally {
      setWasteHistoryLoading(false);
    }
  };

  const fetchStapleList = async () => {
    try {
      setStapleLoading(true);
      const resp = await fetch(`${API_BASE}/api/inventory/staples`, {
        credentials: 'include',
        headers: getAuthHeaders({ Accept: 'application/json' })
      });
      if (resp.ok) {
        const data = await resp.json();
        setStapleList(data || []);
      }
    } catch (err) {
      console.error('Staple list fetch error:', err);
    } finally {
      setStapleLoading(false);
    }
  };

  const openStapleModal = () => {
    setIsStapleModalOpen(true);
    if (stapleList.length === 0) {
      fetchStapleList();
    }
  };

  const getBackendLineTotal = (item) => {
    const fromDto = parseFloat(item?.lineTotal);
    if (!Number.isNaN(fromDto)) return fromDto;
    const legacy = parseFloat(item?.purchasePrice || item?.purchase_price || 0);
    return Number.isNaN(legacy) ? 0 : legacy;
  };

  const generateSavingsAdvice = (item) => {
    const ingredientName = item.ingredientName || item.ingredient_name || item.ingredient?.name || 'Ürün';
    const price = getBackendLineTotal(item);
    const amount = parseFloat(item.amount || item.remaining_amount || 1);

    if (item.category === 'Baby' || item.category === 'Bebek') {
      return `Porsiyonu küçült, bebek sağlığı için israfı sıfırla. 👶`;
    } else if (item.waste_rate > 0.2) {
      const wastePercent = (item.waste_rate * 100).toFixed(0);
      const monthlyLoss = Math.round(price * item.waste_rate * 3);
      // "Birim fiyatının stoğa oranı" logic as requested
      const priceToStockRatio = Math.min(Math.max(Math.round((price / amount)), 10), 50);

      return `Şefim!; Son 3 alımında da ${ingredientName}'in %${wastePercent}'i çöpe gitti. 📉 Bu alışkanlığın sana ayda ${formatCurrency(monthlyLoss)} kaybettiriyor. Alımını %${priceToStockRatio} düşürerek bu parayı kumbarana (Puanına) ekleyebilirsin.`;
    } else if (item.shelf_life_days <= 5) {
      return `Saklama yöntemini (Turuncu Kitap) gözden geçir veya az al. 📚`;
    }
    return `Bu ürün için israf analizi yapılıyor... ✨`;
  };

  const openWasteHistory = () => {
    setIsWasteHistoryOpen(true);
    fetchWasteHistory();
  };

  // Edit işlemleri
  const openEditModal = (item) => {
    setModalMode('edit');
    setEditingItem(item);
    setEditForm({
      remaining_amount: item.remaining_amount,
      unit: item.unit,
      expiry_date: formatDateForInput(item.expiry_date),
      purchase_price: item.purchase_price || '',
      storage_location: item.storage_location || '',
      main_zone: item.main_zone,
      sub_zone: item.sub_zone,
      ingredient_name: item.ingredient_name || '',
      brand: item.brand || '',
      is_pasteurized: item.ingredient?.nutrition_data?.is_pasteurized !== false, // Default to true if missing
      _default_price: 0,
      _default_price_unit: '',
      _unit_price: 0,
      _auto_price: false,
      _price_locked: true,
      _zone_locked: true
    });
    setEditModalOpen(true);
  };

  const openAddModal = () => {
    setModalMode('add');
    setEditingItem(null);
    setEditForm({
      remaining_amount: '1',
      unit: 'adet',
      expiry_date: '',
      purchase_price: '',
      storage_location: '',
      main_zone: activeZone,
      sub_zone: zones[activeZone]?.subZones?.find(z => z !== 'Tümü') || activeSubZone,
      ingredient_name: '', // Yeni alan eklendi
      brand: '',
      is_pasteurized: true, // Default for new items
      _default_price: 0,
      _default_price_unit: '',
      _unit_price: 0,
      _auto_price: false,
      _price_locked: false,
      _zone_locked: false
    });
    setEditModalOpen(true);
  };

  const openDeleteModal = (item) => {
    // Show centered modal with waste confirmation
    Swal.fire({
      title: `${item.ingredient_name} siliniyor`,
      html: `
        <div class="text-center">
          <p class="text-base text-gray-600 dark:text-gray-400 mb-4">
            İsraf olarak işaretlendi. 10 saniye içinde otomatik silinecek.
          </p>
          <div class="flex flex-col gap-3 mt-4">
            <button id="used-btn" class="w-full px-4 py-3 bg-green-500 text-white rounded-xl text-base font-bold hover:bg-green-600 transition-colors flex items-center justify-center gap-2">
              <span class="text-xl">🍳</span>
              <span>Kullanıldı</span>
            </button>
            <button id="rescued-btn" class="w-full px-4 py-3 bg-blue-500 text-white rounded-xl text-base font-bold hover:bg-blue-600 transition-colors flex items-center justify-center gap-2">
              <span class="text-xl">♻️</span>
              <span>Verdim</span>
            </button>
            <button id="mistake-btn" class="w-full px-4 py-3 bg-gray-500 text-white rounded-xl text-base font-bold hover:bg-gray-600 transition-colors flex items-center justify-center gap-2">
              <span class="text-xl">✏️</span>
              <span>Yanlış - İptal</span>
            </button>
          </div>
        </div>
      `,
      icon: 'warning',
      showConfirmButton: false,
      timer: 10000,
      timerProgressBar: true,
      background: isDarkMode ? '#1e293b' : '#fff',
      color: isDarkMode ? '#e2e8f0' : '#1f2937',
      width: '400px',
      didOpen: () => {
        // Add click handlers for quick actions
        document.getElementById('used-btn')?.addEventListener('click', () => {
          Swal.close();
          deleteItemWithReason(item, 'used');
        });
        document.getElementById('rescued-btn')?.addEventListener('click', () => {
          Swal.close();
          deleteItemWithReason(item, 'rescued');
        });
        document.getElementById('mistake-btn')?.addEventListener('click', () => {
          Swal.close();
          Swal.fire({
            icon: 'info',
            title: 'İptal edildi',
            text: 'Ürün silinmedi',
            timer: 2000,
            showConfirmButton: false,
            background: isDarkMode ? '#1e293b' : '#fff',
            color: isDarkMode ? '#e2e8f0' : '#1f2937'
          });
        });
      }
    }).then((result) => {
      // If timer ran out (no button clicked), delete as waste
      if (result.dismiss === Swal.DismissReason.timer) {
        deleteItemWithReason(item, 'waste');
      }
    });
  };

  const deleteItemWithReason = async (item, reason) => {
    try {
      const response = await fetch(`${API_BASE}/api/inventory/${item.id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: getAuthHeaders({
          'Content-Type': 'application/json'
        }),
        body: JSON.stringify({ waste_reason: reason })
      });

      if (response.ok) {
        const reasonLabels = {
          waste: 'israf',
          used: 'kullanıldı',
          rescued: 'verildi'
        };

        Swal.fire({
          icon: 'success',
          title: 'Silindi!',
          text: `${item.ingredient_name} ${reasonLabels[reason]} olarak kaydedildi`,
          timer: 2000,
          showConfirmButton: false
        });

        await Promise.all([fetchInventoryData(), fetchAllInventoryData()]);
      } else {
        throw new Error('Silme başarısız');
      }
    } catch (error) {
      console.error('Delete error:', error);
      Swal.fire({
        icon: 'error',
        title: 'Hata!',
        text: 'Ürün silinemedi',
        confirmButtonText: 'Tamam'
      });
    }
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditingItem(null);
    setModalMode('edit');
    setEditForm({
      remaining_amount: '',
      unit: '',
      expiry_date: '',
      purchase_price: '',
      storage_location: '',
      main_zone: '',
      sub_zone: '',
      ingredient_name: '', // Yeni alan eklendi
      brand: '',
      _default_price: 0,
      _default_price_unit: '',
      _unit_price: 0,
      _auto_price: false,
      _price_locked: false,
      _zone_locked: false
    });
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setDeletingItem(null);
  };

  const confirmOpenItem = (item) => {
    setOpenConfirmItem(item);
  };

  const handleOpenConfirm = async () => {
    if (openConfirmItem) {
      await markAsOpened(openConfirmItem);
      setOpenConfirmItem(null);
    }
  };

  const markAsOpened = async (item) => {
    try {
      setLoading(true);
      console.log('📦 Ürün açıldı olarak işaretleniyor:', item.ingredient_name);

      const response = await fetch(`${API_BASE}/api/inventory/${item.id}/mark-opened`, {
        method: 'PUT',
        credentials: 'include',
        headers: getAuthHeaders({
          Accept: 'application/json',
          'Content-Type': 'application/json'
        }),
        body: JSON.stringify({ opened: true })
      });

      if (response.ok) {
        console.log('✅ Ürün açık olarak işaretlendi ve yeni SKT hesaplandı');
        await Promise.all([fetchInventoryData(), fetchAllInventoryData()]);
      } else {
        let errorMessage = 'İşaretleme başarısız';
        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const errorData = await response.json();
            errorMessage = errorData.error || errorData.message || errorMessage;
          } else {
            const errorText = await response.text();
            errorMessage = errorText || errorMessage;
          }
        } catch (parseError) {
          console.error('❌ İşaretleme hata içeriği okunamadı:', parseError);
        }
        throw new Error(errorMessage);
      }
    } catch (err) {
      console.error('❌ Ürün işaretlenirken hata:', err);
      setError('Ürün işaretlenirken hata oluştu: ' + err.message);
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  const deleteItem = async () => {
    if (!deletingItem) return;

    try {
      setLoading(true);
      console.log('🗑️ Silme işlemi başlatılıyor:', {
        id: deletingItem.id,
        name: deletingItem.ingredient_name
      });

      const response = await fetch(`${API_BASE}/api/inventory/${deletingItem.id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: getAuthHeaders({
          Accept: 'application/json',
          'Content-Type': 'application/json'
        })
      });

      console.log('🔍 DELETE response status:', response.status);

      if (response.ok) {
        console.log('✅ Ürün başarıyla silindi');
        // Inventory'yi yeniden yükle
        await Promise.all([fetchInventoryData(), fetchAllInventoryData(), fetchWastedStats()]);
        closeDeleteModal();
      } else {
        // Hata detaylarını al
        let errorMessage = 'Silme işlemi başarısız';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
          console.error('❌ Server error details:', errorData);
        } catch (parseError) {
          // JSON parse edilemezse text olarak oku
          const errorText = await response.text();
          console.error('❌ Server error text:', errorText);
          errorMessage = `Server error: ${response.status}`;
        }
        throw new Error(errorMessage);
      }
    } catch (err) {
      console.error('❌ Ürün silinirken hata:', err);
      setError('Ürün silinirken hata oluştu: ' + err.message);
      // Error 3 saniye sonra kaybolsun
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  // 🚀 V19.2 PERFORMANCE REFINEMENT
  const lastClassifiedRef = useRef('');
  const abortControllerRef = useRef(null);

  const handleEditFormChange = async (field, value) => {
    setEditForm(prev => {
      const newState = { ...prev, [field]: value };
      const computeAutoPrice = (amountValue, unitValue, state) => {
        const defaultPrice = Number(state?._default_price || 0);
        const defaultPriceUnit = String(state?._default_price_unit || '').trim();
        if (defaultPrice > 0 && defaultPriceUnit) {
          const estimated = estimateSeedLinePrice({
            amount: amountValue,
            unit: unitValue,
            defaultPrice,
            defaultPriceUnit
          });
          return estimated > 0 ? estimated.toFixed(2) : '';
        }

        const unitPrice = Number(state?._unit_price || 0);
        const numericAmount = parseFloat(amountValue) || 0;
        if (unitPrice > 0 && numericAmount > 0) {
          return (numericAmount * unitPrice).toFixed(2);
        }

        return '';
      };

      if (field === 'main_zone') {
        const zoneData = zones[value];
        if (zoneData?.subZones?.length > 0) {
          newState.sub_zone = subZoneMapping[zoneData.subZones[0]] || zoneData.subZones[0];
        }
        newState._zone_locked = true;
      }

      if (field === 'sub_zone') {
        newState._zone_locked = true;
      }

      if (field === 'purchase_price') {
        newState._price_locked = true;
        newState._auto_price = false;
      }

      // 💰 Miktar değiştiğinde fiyatı yeniden hesapla
      if ((field === 'remaining_amount' || field === 'unit') && prev._auto_price && !prev._price_locked) {
        const nextAmount = field === 'remaining_amount' ? value : newState.remaining_amount;
        const nextUnit = field === 'unit' ? value : newState.unit;
        newState.purchase_price = computeAutoPrice(nextAmount, nextUnit, prev);
      }

      return newState;
    });

    // 🧠 AI & SQL MEMORY: Auto-fill fields based on name
    if (field === 'ingredient_name') {
      const trimmedValue = value.trim();

      // 1. Debounced Search & Classify
      if (searchTimeout) clearTimeout(searchTimeout);

      const timeout = setTimeout(async () => {
        if (trimmedValue.length < 2) {
          setOntologyResults([]);
          return;
        }

        // 🛡️ Abort previous request (Race Condition Protection)
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();

        try {
          // A. Ontology Search (Visual results)
          const searchRes = await fetch(`${API_BASE_URL}/api/inventory/search-ontology?q=${encodeURIComponent(trimmedValue)}`, {
            headers: getAuthHeaders(),
            signal: abortControllerRef.current.signal
          });
          if (searchRes.ok) {
            const data = await searchRes.json();
            setOntologyResults(data);
          }

          // B. AI Classify (Auto-fill) - Only in 'add' mode & if name changed since last classify
          if (modalMode === 'add' && lastClassifiedRef.current !== trimmedValue) {
            console.log(`🧠 Triggering classification for: "${trimmedValue}"`);

            // Expiry calculation (Local)
            calculateExpiryDate(trimmedValue).then(date => {
              setEditForm(prev => ({ ...prev, expiry_date: date }));
            });

            const classifyRes = await fetch(`${API_BASE_URL}/api/inventory/classify?name=${encodeURIComponent(trimmedValue)}`, {
              headers: getAuthHeaders(),
              signal: abortControllerRef.current.signal
            });
            const data = await classifyRes.json();

            if (data && !data.error) {
              lastClassifiedRef.current = trimmedValue; // Memoize success
              setEditForm(prev => ({
                ...prev,
                main_zone: prev._zone_locked ? prev.main_zone : (data.main_zone || prev.main_zone),
                sub_zone: prev._zone_locked ? prev.sub_zone : (data.sub_zone || prev.sub_zone),
                unit: data.base_unit || prev.unit,
                purchase_price: prev._price_locked
                  ? prev.purchase_price
                  : (
                    prev._default_price > 0
                      ? (
                        estimateSeedLinePrice({
                          amount: prev.remaining_amount || 1,
                          unit: data.base_unit || prev.unit,
                          defaultPrice: prev._default_price,
                          defaultPriceUnit: prev._default_price_unit
                        }) > 0
                          ? estimateSeedLinePrice({
                            amount: prev.remaining_amount || 1,
                            unit: data.base_unit || prev.unit,
                            defaultPrice: prev._default_price,
                            defaultPriceUnit: prev._default_price_unit
                          }).toFixed(2)
                          : prev.purchase_price
                      )
                      : (prev.purchase_price || data.estimated_price || '')
                  ),
                _unit_price: data.unit_price || 0,
                _auto_price: prev._price_locked ? prev._auto_price : Boolean(prev._default_price > 0 || data.unit_price > 0)
              }));
            }
          }
        } catch (err) {
          if (err.name === 'AbortError') {
            console.log('🛑 Request aborted due to new input');
          } else {
            console.warn('Performans iyileştirme hatası:', err);
          }
        }
      }, 450); // Uzatılmış 450ms Debounce

      setSearchTimeout(timeout);
    }
  };

  const selectFromOntology = (item) => {
    setEditForm(prev => ({
      ...prev,
      ingredient_name: item.name,
      unit: item.unit || prev.unit || 'adet',
      purchase_price: (!prev._price_locked && Number(item.defaultPrice || 0) > 0 && String(item.defaultPriceUnit || '').trim())
        ? estimateSeedLinePrice({
          amount: prev.remaining_amount || 1,
          unit: item.unit || prev.unit || 'adet',
          defaultPrice: Number(item.defaultPrice || 0),
          defaultPriceUnit: String(item.defaultPriceUnit || '').trim()
        }).toFixed(2)
        : prev.purchase_price,
      _default_price: Number(item.defaultPrice || 0),
      _default_price_unit: String(item.defaultPriceUnit || '').trim(),
      _auto_price: !prev._price_locked && Number(item.defaultPrice || 0) > 0
    }));
    setOntologyResults([]);
    handleEditFormChange('ingredient_name', item.name);
  };


  const handleBakliyatTypeChange = (type) => {
    let currentName = editForm.ingredient_name;
    // Mevcut parantezleri temizle
    currentName = currentName.replace(' (Haşlanmış)', '').replace(' (Konserve)', '').trim();

    if (type === 'cooked') {
      currentName = `${currentName} (Haşlanmış)`;
    }

    // Doğrudan state'i güncelle
    setEditForm(prev => ({ ...prev, ingredient_name: currentName }));
  };

  const saveEditedItem = async () => {
    try {
      setLoading(true);

      if (modalMode === 'edit') {
        if (!editingItem) return;

        console.log('📝 Güncelleme işlemi başlatılıyor:', {
          id: editingItem.id,
          changes: editForm
        });

        const response = await fetch(`${API_BASE}/api/inventory/${editingItem.id}`, {
          method: 'PUT',
          credentials: 'include',
          headers: getAuthHeaders({
            Accept: 'application/json',
            'Content-Type': 'application/json; charset=utf-8'
          }),
          body: JSON.stringify({
            remaining_amount: parseFloat(editForm.remaining_amount) || editingItem.remaining_amount,
            unit: editForm.unit || editingItem.unit,
            expiry_date: editForm.expiry_date || editingItem.expiry_date,
            purchase_price: editForm.purchase_price ? parseFloat(editForm.purchase_price) : editingItem.purchase_price,
            storage_location: editForm.storage_location || editingItem.storage_location,
            main_zone: editForm.main_zone || editingItem.main_zone,
            sub_zone: editForm.sub_zone || editingItem.sub_zone,
            ingredient_name: editForm.ingredient_name || editingItem.ingredient_name,
            brand: editForm.brand,
            is_pasteurized: editForm.is_pasteurized
          })
        });

        if (response.ok) {
          console.log('✅ Ürün başarıyla güncellendi');
        } else {
          let errorMessage = 'Güncelleme başarısız';
          try {
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
              const errorData = await response.json();
              errorMessage = errorData.error || errorData.message || errorMessage;
            } else {
              const errorText = await response.text();
              errorMessage = errorText || errorMessage;
            }
          } catch (parseError) {
            console.error('❌ Güncelleme hata içeriği okunamadı:', parseError);
          }
          throw new Error(errorMessage);
        }
      } else if (modalMode === 'add') {
        if (!editForm.ingredient_name.trim()) {
          throw new Error('Ürün adı zorunludur');
        }

        console.log('➕ Yeni ürün ekleme işlemi başlatılıyor:', editForm);

        const response = await fetch(`${API_BASE}/api/inventory`, {
          method: 'POST',
          credentials: 'include',
          headers: getAuthHeaders({
            Accept: 'application/json',
            'Content-Type': 'application/json; charset=utf-8'
          }),
          body: JSON.stringify({
            ingredient_name: editForm.ingredient_name.trim(),
            remaining_amount: parseFloat(editForm.remaining_amount) || 1,
            unit: editForm.unit || 'adet',
            expiry_date: editForm.expiry_date || null,
            purchase_price: editForm.purchase_price ? parseFloat(editForm.purchase_price) : null,
            storage_location: editForm.storage_location || '',
            main_zone: editForm.main_zone || zoneMapping[activeZone] || activeZone,
            sub_zone: editForm.sub_zone || subZoneMapping[activeSubZone] || activeSubZone,
            brand: editForm.brand,
            is_pasteurized: editForm.is_pasteurized
          })
        });

        console.log('🔍 POST response status:', response.status);

        if (response.ok) {
          console.log('✅ Yeni ürün başarıyla eklendi');
        } else {
          let errorMessage = 'Ekleme başarısız';
          try {
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
              const errorData = await response.json();
              errorMessage = errorData.error || errorData.message || errorMessage;
              console.error('❌ Server error details:', errorData);
            } else {
              const errorText = await response.text();
              console.error('❌ Server error text:', errorText);
              errorMessage = errorText || `Server error: ${response.status}`;
            }
          } catch (parseError) {
            console.error('❌ Ekleme hata içeriği okunamadı:', parseError);
          }
          throw new Error(errorMessage);
        }
      }

      // Inventory'yi yeniden yükle
      await Promise.all([fetchInventoryData(), fetchAllInventoryData()]);
      closeEditModal();

      if (modalMode === 'add') {
        triggerBabyPrompt();
      }
    } catch (err) {
      console.error(`❌ Ürün ${modalMode === 'edit' ? 'güncellenirken' : 'eklenirken'} hata:`, err);
      setError(`Ürün ${modalMode === 'edit' ? 'güncellenirken' : 'eklenirken'} hata oluştu: ` + err.message);
      // Error 3 saniye sonra kaybolsun
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  // SKT durumu hesaplama
  const getExpiryStatus = (expiryDate) => {
    if (!expiryDate) return { status: 'unknown', text: 'Bilinmiyor', color: 'gray', tooltip: 'Son kullanma tarihi belirtilmemiş' };

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expiry = new Date(expiryDate);
    expiry.setHours(0, 0, 0, 0);

    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return { status: 'expired', text: 'Geçmiş', color: 'red', icon: 'dangerous', tooltip: 'Bu ürünün son kullanma tarihi geçmiş - tüketmeyin!' };
    if (diffDays === 0) return { status: 'today', text: 'Bugün', color: 'red', icon: 'warning', tooltip: 'Bu ürün bugün bitiyor - hemen tüketin!' };
    if (diffDays <= 3) return { status: 'warning', text: `${diffDays} gün kaldı`, color: 'orange', icon: 'hourglass_top', tooltip: `Bu ürün ${diffDays} gün içinde bozulacak - öncelikli tüketin` };
    if (diffDays <= 7) return { status: 'caution', text: `${diffDays} gün kaldı`, color: 'yellow', icon: 'schedule', tooltip: `Bu ürün ${diffDays} gün içinde bozulacak` };
    return { status: 'good', text: `${diffDays} gün kaldı`, color: 'emerald', icon: 'check_circle', tooltip: `Bu ürün ${diffDays} gün daha taze kalacak` };
  };

  // Bebek güvenlik badge'i
  const getBabySafetyBadge = (item) => {
    // Determine baby status from multiple possible flags
    const babyActive = Boolean(userProfile?.hasBaby || userProfile?.bebek_var_mi || hasChildren);
    if (!babyActive) return null;

    const nutritionData = item.ingredient?.nutrition_data || {};
    const itemName = (item.ingredient_name || item.ingredient?.name || '').toLowerCase();
    
    // Hardcoded Critical Hazards (Logic Authority: SS-06 Baby Safety Rules)
    const isHoney = itemName.includes('bal') && !itemName.includes('balık') && !itemName.includes('kabak');
    
    const minMonth = nutritionData.min_month || (isHoney ? 12 : 6);
    const isSafe = nutritionData.is_baby_safe !== false && !isHoney;
    const safetyReason = isHoney ? 'honey_risk' : nutritionData.baby_safety_reason;

    // Get baby age from profile
    const babyAgeMonths = userProfile?.baby_age_months || babyProfile?.baby_age_months || 12;

    if (!isSafe || isHoney) {
      let tooltipMessage = "Bu ürün bebekler için güvenli değil";
      let alertTitle = "Bebek Güvenliği Bilgilendirmesi";
      let alertIcon = "warning";

      // Detaylı güvenlik mesajları
      const bName = userProfile?.babyName || 'Bebeğiniz';
      const safetyMessages = {
        'honey_risk': `⚠️ Dikkat! ${bName} ${babyAgeMonths} aylık. ${bName} şu anda 12 aydan küçük olduğu için bal önerilmez (Botulizm riski).`,
        'botulizm_riski': `⚠️ Dikkat! ${bName} ${babyAgeMonths} aylık. ${bName} şu anda 12 aydan küçük olduğu için bal önerilmez.`,
        'bakteri_riski': `⚠️ ${bName} ${babyAgeMonths} aylık. Bu ürün pastörize edilmemiş süt içerebilir - bakteri riski nedeniyle ${minMonth}+ ay önerilir.`,
        'cig_yumurta_ve_asit': `⚠️ Bu ürün çiğ yumurta veya asidik içerik barındırabilir. ${bName} için ${minMonth}+ ay önerilir.`,
        'bogulma_riski': `⚠️ ${bName} ${babyAgeMonths} aylık. Bu ürün sert parçalar içerir - boğulma riski nedeniyle ${minMonth}+ ay (3 yaş) önerilir.`,
        'konserve_balik_riski': `⚠️ Konserve ürünlerdeki metal birikimi ve yüksek tuz oranı ${bName} için uygun değildir.`,
        'bobrek_ve_metabolik_stres': `⚠️ Bu ürün ilave tuz veya şeker içerir. ${bName}'in böbreklerini yormamak için ${minMonth}+ ay önerilir.`,
        'yumurta_alerjisi_riski': `⚠️ Yumurta güçlü bir alerjendir. ${bName}'in ilk tadımında dikkatli olunması önerilir (Sadece yumurta sarısı ile başlanabilir).`
      };

      if (safetyMessages[safetyReason]) {
        tooltipMessage = safetyMessages[safetyReason];
      } else if (minMonth > babyAgeMonths) {
        tooltipMessage = `⚠️ ${bName} ${babyAgeMonths} aylık. Bu ürün ${minMonth}+ ay önerilir.`;
      }

      // baby_flags kontrolü (Ekstra uyarılar)
      const flags = nutritionData.baby_flags || [];
      if (flags.includes('fully_cooked_required')) {
        tooltipMessage += "\n🔥 Bu ürünü bebeğinize vermeden önce TAM PİŞMİŞ olduğundan emin olun.";
      }

      return (
        <div
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-red-50 text-red-600 border border-red-100/50 cursor-pointer hover:bg-red-100 transition-colors"
          title={tooltipMessage}
          onClick={(e) => {
            e.stopPropagation();
            Swal.fire({
              title: alertTitle,
              text: tooltipMessage,
              icon: alertIcon,
              confirmButtonText: 'Anladım',
              confirmButtonColor: '#ef4444',
              background: isDarkMode ? '#1e293b' : '#fff',
              color: isDarkMode ? '#e2e8f0' : '#1f2937'
            });
          }}
        >
          <span className="material-symbols-outlined text-[12px] fill-1">child_care</span>
          <span className="text-[9px] font-bold">Tehlikeli</span>
        </div>
      );
    }

    if (minMonth > babyAgeMonths) {
      const infoText = `Bebeğiniz ${babyAgeMonths} aylık. Bu ürün ${minMonth}+ ay önerilir`;
      return (
        <div
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-orange-50 text-orange-600 border border-orange-100/50 cursor-pointer hover:bg-orange-100 transition-colors"
          title={infoText}
          onClick={(e) => {
            e.stopPropagation();
            Swal.fire({
              title: 'Yaş Bilgilendirmesi',
              text: infoText,
              icon: 'info',
              confirmButtonText: 'Tamam',
              confirmButtonColor: '#f97316',
              background: isDarkMode ? '#1e293b' : '#fff',
              color: isDarkMode ? '#e2e8f0' : '#1f2937'
            });
          }}
        >
          <span className="material-symbols-outlined text-[12px] fill-1">child_care</span>
          <span className="text-[9px] font-bold">{minMonth}+ ay</span>
        </div>
      );
    }

    if (nutritionData.is_baby_safe === true) {
      const infoText = `Bu ürün ${minMonth} aylık ve üzeri bebekler için güvenlidir`;
      return (
        <div
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-emerald-50 text-emerald-600 border border-emerald-100/50 cursor-pointer hover:bg-emerald-100 transition-colors"
          title={infoText}
          onClick={(e) => {
            e.stopPropagation();
            Swal.fire({
              title: 'Bebek Güvenli',
              text: infoText,
              icon: 'success',
              confirmButtonText: 'Harika',
              confirmButtonColor: '#10b981',
              background: isDarkMode ? '#1e293b' : '#fff',
              color: isDarkMode ? '#e2e8f0' : '#1f2937'
            });
          }}
        >
          <span className="material-symbols-outlined text-[12px] fill-1">child_care</span>
          <span className="text-[9px] font-bold">{minMonth}+ ay</span>
        </div>
      );
    }

    return null;
  };

  // Helper to check for baby safety risk without JSX
  const checkIsBabyRisky = (item) => {
    const bName = userProfile?.babyName || babyProfile?.babyName || 'Bebeğiniz';
    const babyActive = Boolean(userProfile?.hasBaby || userProfile?.bebek_var_mi || hasChildren);
    if (!babyActive) return false;

    const nutritionData = item.ingredient?.nutrition_data || {};
    const itemName = (item.ingredient_name || item.ingredient?.name || '').toLowerCase();
    const isHoney = itemName.includes('bal') && !itemName.includes('balık') && !itemName.includes('kabak');
    
    const minMonth = nutritionData.min_month || (isHoney ? 12 : 6);
    const isSafe = nutritionData.is_baby_safe !== false && !isHoney;
    
    const babyAgeMonths = userProfile?.baby_age_months ?? 12;
    return !isSafe || minMonth > babyAgeMonths;
  };

  const checkIsAllergenRisky = (item) => {
    const nutrition = item.ingredient?.nutrition_data || {};
    const userAllergies = [
      ...(userProfile?.allergies || []),
      ...(userProfile?.dietaryRestrictions || []),
      ...(userProfile?.intakeSurvey?.healthConditions || [])
    ].map(a => a.toLowerCase());

    const itemName = (item.ingredient_name || item.ingredient?.name || '').toLowerCase();

    // 1. Check Nutrition Allergens
    const nutritionAllergens = Array.isArray(nutrition.allergens) ? nutrition.allergens : (nutrition.allergen_group && nutrition.allergen_group !== 'none' ? [nutrition.allergen_group] : []);
    const isNutritionRisky = nutritionAllergens.some(groupKey => {
      if (groupKey === 'none') return false;
      return userAllergies.some(a =>
        a === groupKey.toLowerCase() ||
        (groupKey === 'gluten' && a.includes('gluten')) ||
        (groupKey === 'nuts' && (a.includes('fındık') || a.includes('fıstık'))) ||
        (groupKey === 'dairy' && (a.includes('süt') || a.includes('laktor')))
      );
    });

    // 2. Check Brand Allergens
    const brandRisksRaw = item.brand_allergen_risks || item.brandAllergenRisks || [];
    const brandRisks = Array.isArray(brandRisksRaw) ? brandRisksRaw : (typeof brandRisksRaw === 'string' ? JSON.parse(brandRisksRaw) : []);
    const isBrandRisky = brandRisks.some(risk =>
      userAllergies.some(a => a.includes(risk.toLowerCase()) || risk.toLowerCase().includes(a.toLowerCase()))
    );

    // 3. Special case for generic 'bal' vs allergen 'bal' (if user allergic to honey)
    const isHoneyAllergenMatch = itemName.includes('bal') && !itemName.includes('balık') && !itemName.includes('kabak') && userAllergies.some(a => a.includes('bal'));

    return isNutritionRisky || isBrandRisky || isHoneyAllergenMatch;
  };

  // Alerjen uyarı badge'i
  const getAllergenBadge = (item) => {
    const nutritionData = item?.ingredient?.nutrition_data || {};
    const itemName = item?.ingredient_name || item?.ingredient?.name || '';
    
    // Normalization: Support new 'allergens' array OR legacy 'allergen_group' string
    let allergens = [];
    if (Array.isArray(nutritionData.allergens) && nutritionData.allergens.length > 0) {
      allergens = nutritionData.allergens;
    } else if (nutritionData.allergen_group && nutritionData.allergen_group !== 'none') {
      allergens = [nutritionData.allergen_group];
    }

    // Hardcoded check for Honey if nutritionData is missing
    const lowerItemName = itemName.toLowerCase();
    if (allergens.length === 0 && lowerItemName.includes('bal') && !lowerItemName.includes('balık') && !lowerItemName.includes('kabak')) {
      allergens = ['honey'];
    }

    if (allergens.length === 0) return null;

    const allergenMap = {
      'dairy': 'Süt',
      'nuts': 'Fındık',
      'egg': 'Yumurta', // Map 'egg' (backend) to 'Yumurta' (UI)
      'egg_white': 'Yumurta',
      'high_allergy_fruit': 'Asitli',
      'gluten': 'Gluten',
      'soy': 'Soya',
      'fish': 'Balık',
      'honey': 'Bal'
    };

    // Check against user profile (both arrays)
    const userAllergies = [
      ...(userProfile?.allergies || []),
      ...(userProfile?.dietaryRestrictions || []),
      ...(userProfile?.intakeSurvey?.healthConditions || [])
    ].map(a => a.toLowerCase());

    return (
      <div className="flex flex-wrap gap-1">
        {allergens.map((groupKey, idx) => {
          if (groupKey === 'none') return null;
          const allergenName = allergenMap[groupKey] || groupKey;

          // Check risk for THIS specific allergen
          const isRisky = userAllergies.some(a =>
            a === groupKey.toLowerCase() ||
            a === allergenName.toLowerCase() ||
            (groupKey === 'gluten' && a.includes('gluten')) ||
            (groupKey === 'nuts' && (a.includes('fındık') || a.includes('fıstık'))) ||
            (groupKey === 'dairy' && (a.includes('süt') || a.includes('laktor')))
          );

          // Self-Evident Check: If the product IS the allergen (e.g. "Yumurta" contains "Yumurta"), 
          // and it is NOT a risk for the user, do not show a warning badge.
          // We only warn if it's hidden or if it's a risk.
          if (!isRisky && lowerItemName && lowerItemName.includes(allergenName.toLowerCase())) {
            return null;
          }

          if (isRisky) {
            const riskMsg = `⚠️ DİKKAT! Senin ${allergenName} hassasiyetin var!`;
            return (
              <div
                key={idx}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-red-50 text-red-600 border border-red-100/50 cursor-pointer animate-pulse"
                title={riskMsg}
                onClick={(e) => {
                  e.stopPropagation();
                  Swal.fire({
                    title: 'Alerjen Riski!',
                    text: `⚠️ DİKKAT! Senin ${allergenName} hassasiyetin var! Şefim; daha fazla güvenlik ve alerjan dostu yemek tarifleri için premium satın alabilirsiniz.`,
                    icon: 'error',
                    confirmButtonText: 'Anladım',
                    background: isDarkMode ? '#1e293b' : '#fff',
                    color: isDarkMode ? '#e2e8f0' : '#1f2937'
                  });
                }}
              >
                <span className="material-symbols-outlined text-[12px] fill-1">dangerous</span>
                <span className="text-[9px] font-bold">RİSKLİ: {allergenName.toUpperCase()}</span>
              </div>
            );
          }

          // Self-Evident Check (Redundant check if loop not broken, but safe)
          if (!isRisky && lowerItemName && lowerItemName.includes(allergenName.toLowerCase())) {
            return null;
          }

          const warnMsg = `Bu üründe genel olarak ${allergenName} bulunur`;

          // Check if this allergen makes the item unsafe for baby
          const babyAgeMonths = babyProfile?.baby_age_months || 12;
          const minMonth = nutritionData.min_month || 6;
          const isSafe = nutritionData.is_baby_safe !== false;
          const safetyReason = nutritionData.baby_safety_reason;

          let babySafetyWarning = null;
          const bName = userProfile?.babyName || 'Bebeğiniz';
          if (hasChildren && (!isSafe || minMonth > babyAgeMonths)) {
            // Baby-specific warnings for allergens
            const babySafetyMessages = {
              'bakteri_riski': `⚠️ ${bName} ${babyAgeMonths} aylık. ${itemName} pastörize edilmemiş süt içerebilir - bakteri riski nedeniyle ${minMonth}+ ay önerilir.`,
              'yumurta_alerjisi_riski': `⚠️ ${itemName} güçlü bir alerjendir. ${bName}'in ilk tadımında dikkatli olunması önerilir (Sadece yumurta sarısı ile başlanabilir).`
            };

            if (babySafetyMessages[safetyReason]) {
              babySafetyWarning = babySafetyMessages[safetyReason];
            } else if (minMonth > babyAgeMonths) {
              babySafetyWarning = `⚠️ ${bName} ${babyAgeMonths} aylık. ${itemName} ${allergenName} içerdiği için ${minMonth}+ ay önerilir.`;
            }
          }

          return (
            <div
              key={idx}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-amber-50 text-amber-600 border border-amber-100/50 cursor-pointer"
              title={babySafetyWarning || warnMsg}
              onClick={(e) => {
                e.stopPropagation();
                Swal.fire({
                  title: babySafetyWarning ? 'Bebek Güvenliği Bilgilendirmesi' : 'Alerjen Bilgisi',
                  text: babySafetyWarning || warnMsg,
                  icon: babySafetyWarning ? 'warning' : 'info',
                  confirmButtonText: 'Tamam',
                  confirmButtonColor: babySafetyWarning ? '#ef4444' : '#d97706',
                  background: isDarkMode ? '#1e293b' : '#fff',
                  color: isDarkMode ? '#e2e8f0' : '#1f2937'
                });
              }}
            >
              <span className="material-symbols-outlined text-[12px] fill-1">warning</span>
              <span className="text-[9px] font-bold">{allergenName}</span>
            </div>
          );
        })}
      </div>
    );
  };

  // Data source selection
  const sourceData = useMemo(() => {
    console.log('🗂️ sourceData filtering:', { filterMode, searchTerm: !!searchTerm, allInventoryData: allInventoryData.length, inventoryData: inventoryData.length });

    const hideKitchenStaples = (items = []) => items.filter((item) => {
      if (item?.is_hidden_staple === true) return false;
      const role = String(item?.ingredient?.role || '').toUpperCase();
      return role !== 'STAPLE';
    });

    if (filterMode === 'expiring') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return hideKitchenStaples(allInventoryData).filter(item => {
        if (!item.expiry_date) return false;
        const exp = new Date(item.expiry_date);
        exp.setHours(0, 0, 0, 0);
        const diffDays = Math.ceil((exp - today) / (1000 * 60 * 60 * 24));
        return diffDays <= 3;
      }).sort((a, b) => new Date(a.expiry_date) - new Date(b.expiry_date));
    }
    if (filterMode === 'baby') {
      return hideKitchenStaples(allInventoryData).filter(item => checkIsBabyRisky(item));
    }
    if (filterMode === 'rescued') {
      return hideKitchenStaples(allInventoryData).filter(item => {
        const p = parseFloat(item.purchase_price);
        return item.is_rescued === true || (!isNaN(p) && p < 0);
      });
    }
    if (filterMode === 'allergen') {
      return hideKitchenStaples(allInventoryData).filter(item => checkIsAllergenRisky(item));
    }
    // Search Mode
    if (searchTerm.trim()) return hideKitchenStaples(allInventoryData);
    // Zone Mode (Default)
    return hideKitchenStaples(inventoryData);
  }, [filterMode, searchTerm, allInventoryData, inventoryData]);

  const filteredInventory = useMemo(() => {
    console.log('🔍 filteredInventory sourceData:', sourceData.length, 'items');
    console.log('🔍 Current filterMode:', filterMode, 'searchTerm:', searchTerm);
    return sourceData.filter(item => {
      // Search Filter
      if (searchTerm.trim()) {
        const q = searchTerm.trim().toLowerCase();
        const name = (item.ingredient_name || item.ingredient?.name || '').toLowerCase();
        const brand = (item.brand || '').toLowerCase();
        const location = (item.storage_location || '').toLowerCase();
        return name.includes(q) || brand.includes(q) || location.includes(q);
      }
      return true;
    });
  }, [sourceData, searchTerm]);

  const expiringSoonCount = filteredInventory.filter((item) => {
    if (!item.expiry_date) return false;
    const today = new Date();
    const expiry = new Date(item.expiry_date);
    const diffDays = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    return diffDays >= 0 && diffDays <= 3;
  }).length;

  const openedCount = filteredInventory.filter(item => item.is_opened).length;
  const showStatusColumn = filteredInventory.some(item => item.can_be_opened || item.is_opened);

  const showSafetyColumn = filteredInventory.some(item => {
    const n = item.ingredient?.nutrition_data;
    if (!n) return false;

    // Safety Badge Logic matches getBabySafetyBadge
    let hasSafety = false;
    if (hasChildren) {
      const minMonth = n.min_month || 12;
      const isSafe = n.is_baby_safe !== false;
      // Logic: !isSafe OR minMonth > 12 OR minMonth <= 6
      if (!isSafe || minMonth > 12 || minMonth <= 6) hasSafety = true;
    }

    // Allergen Badge Logic matches getAllergenBadge
    const hasAllergen = n.allergen_group && n.allergen_group !== 'none';

    return hasSafety || hasAllergen;
  });

  const showPriceColumn = filteredInventory.some((item) => {
    const val = getBackendLineTotal(item);
    return Number.isFinite(val) && val !== 0;
  });

  const totalPurchasePrice = inventoryData.reduce((sum, item) => {
    const price = getBackendLineTotal(item);
    return sum + (Number.isNaN(price) ? 0 : price);
  }, 0);

  const zoneUniqueCounts = Object.keys(zones).reduce((acc, zoneKey) => {
    const names = new Set(
      allInventoryData
        .filter((item) => item.main_zone === zoneKey)
        .map((item) => item.ingredient?.name || item.ingredient_name || `item-${item.id}`)
    );
    acc[zoneKey] = names.size;
    return acc;
  }, {});

  const handleBabyModalSubmit = async (babyData) => {
    const ok = await submitBabyData(babyData);
    if (ok) {
      setHasChildren(true);
      setUserProfile(prev => ({ ...prev, ...babyData }));
    }
    setLocalBabyModalOpen(false);
  };

  const handleBabyModalClose = async () => {
    setLocalBabyModalOpen(false);
    await closeBabyModal();
  };

  // --- SAFETY RISK MODALS (V22.12 RESTORED) ---
  const showBabyRiskSummary = () => {
    const bName = userProfile?.babyName || 'Bebeğiniz';
    const riskyItems = allInventoryData.filter(item => checkIsBabyRisky(item));
    const babyAgeMonths = userProfile?.baby_age_months ?? 12;

    if (riskyItems.length === 0) {
      Swal.fire({
        title: 'Harika Haber! ✨',
        text: `${bName} için şu an mutfağınızda herhangi bir riskli ürün bulunmuyor.`,
        icon: 'success',
        confirmButtonText: 'Harika',
        confirmButtonColor: '#059669',
        background: isDarkMode ? '#1e293b' : '#fff',
        color: isDarkMode ? '#e2e8f0' : '#1f2937'
      });
      return;
    }

    const itemsHtml = riskyItems.map(item => {
      const n = item.ingredient?.nutrition_data || {};
      const itemName = (item.ingredient_name || item.ingredient?.name || 'Bilinmeyen');
      const isHoney = itemName.toLowerCase().includes('bal') && !itemName.toLowerCase().includes('balık');
      const minMonth = n.min_month || (isHoney ? 12 : 6);

      let reason = n.baby_safety_reason || 'Yaş sınırı veya içerik riski.';
      if (isHoney) reason = 'Botulizm (Bal Zehirlenmesi) riski nedeniyle 1 yaşından önce KESİNLİKLE verilmemelidir.';
      else if (n.baby_safety_reason === 'bakteri_riski') reason = 'Pastörize edilmemiş süt içeriği / Bakteri riski.';

      return `
        <div class="flex flex-col p-3 mb-2 bg-orange-50 dark:bg-orange-950/20 border border-orange-100 dark:border-orange-900/30 rounded-xl text-left">
          <div class="flex items-center justify-between mb-1">
            <span class="font-bold text-orange-900 dark:text-orange-400 capitalize">${itemName}</span>
            <span class="text-[10px] font-black bg-orange-200 dark:bg-orange-800 px-1.5 py-0.5 rounded uppercase">TEHLİKELİ</span>
          </div>
          <p class="text-[11px] text-orange-700 dark:text-orange-300 leading-tight">
            ${bName} ${babyAgeMonths} aylık, bu ürün için önerilen alt sınır: <b>${minMonth}+ ay</b>
          </p>
          <p class="text-[10px] text-gray-500 dark:text-gray-400 mt-1 italic border-t border-orange-100/50 pt-1">
             ${reason}
          </p>
        </div>
      `;
    }).join('');

    Swal.fire({
      title: `<span class="text-orange-600 font-bold">${bName} İçin Riskli Ürünler</span>`,
      html: `
        <div class="mt-2 max-h-[60vh] overflow-y-auto no-scrollbar pr-1">
          <p class="text-xs text-gray-500 mb-4 text-center leading-relaxed">
            Aşağıdaki ürünler ${bName}'in gelişimi veya güvenliği için şu an uygun olmayabilir.
          </p>
          ${itemsHtml}
        </div>
      `,
      icon: 'warning',
      confirmButtonText: 'Anladım, Dikkat Edeceğim',
      confirmButtonColor: '#ea580c',
      background: isDarkMode ? '#1e293b' : '#fff',
      color: isDarkMode ? '#e2e8f0' : '#1f2937'
    });
  };

  const showAllergenRiskSummary = () => {
    const userAllergies = [
      ...(userProfile?.allergies || []),
      ...(userProfile?.dietaryRestrictions || []),
      ...(userProfile?.intakeSurvey?.healthConditions || [])
    ].map(a => a.toLowerCase());

    const riskyItems = allInventoryData.filter(item => {
      // Logic same as allergen filter
      const nutrition = item.ingredient?.nutrition_data || {};
      const nutritionAllergens = Array.isArray(nutrition.allergens) ? nutrition.allergens : (nutrition.allergen_group && nutrition.allergen_group !== 'none' ? [nutrition.allergen_group] : []);
      const isNutritionRisky = nutritionAllergens.some(groupKey => {
        if (groupKey === 'none') return false;
        return userAllergies.some(a => a === groupKey.toLowerCase() || (groupKey === 'gluten' && a.includes('gluten')) || (groupKey === 'nuts' && (a.includes('fındık') || a.includes('fıstık'))) || (groupKey === 'dairy' && (a.includes('süt') || a.includes('laktor'))));
      });
      const brandRisksRaw = item.brand_allergen_risks || item.brandAllergenRisks || [];
      const brandRisks = Array.isArray(brandRisksRaw) ? brandRisksRaw : (typeof brandRisksRaw === 'string' ? JSON.parse(brandRisksRaw) : []);
      const isBrandRisky = brandRisks.some(risk => userAllergies.some(a => a.includes(risk.toLowerCase()) || risk.toLowerCase().includes(a)));
      return isNutritionRisky || isBrandRisky;
    });

    if (riskyItems.length === 0) {
      Swal.fire({
        title: 'Tam Güvenlik! 🛡️',
        text: 'Mevcut stoklarınızda alerjen profilinizle çakışan bir risk bulunamadı.',
        icon: 'success',
        confirmButtonText: 'Süper',
        confirmButtonColor: '#059669',
        background: isDarkMode ? '#1e293b' : '#fff',
        color: isDarkMode ? '#e2e8f0' : '#1f2937'
      });
      return;
    }

    const itemsHtml = riskyItems.map(item => {
      const itemName = (item.ingredient_name || item.ingredient?.name || 'Bilinmeyen');
      return `
        <div class="flex flex-col p-3 mb-2 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/30 rounded-xl text-left">
          <div class="flex items-center justify-between mb-1">
            <span class="font-bold text-red-900 dark:text-red-400 capitalize">${itemName}</span>
          </div>
          <p class="text-[10px] text-red-700 dark:text-red-300 leading-tight">
            Alerjen veya diyet kısıtlamalarınızla uyuşmuyor.
          </p>
        </div>
      `;
    }).join('');

    Swal.fire({
      title: '<span class="text-red-600 font-bold underline decoration-red-600/30">Alerjen Risk Analizi</span>',
      html: `
        <div class="mt-2 max-h-[60vh] overflow-y-auto no-scrollbar pr-1">
          <p class="text-xs text-gray-500 mb-4 text-center leading-relaxed">
            Profilinizdeki alerjenler ile stoktaki ürünler arasında çakışma tespit edildi.
          </p>
          ${itemsHtml}
        </div>
      `,
      icon: 'error',
      confirmButtonText: 'Yardımcı Oldu, Teşekkürler',
      confirmButtonColor: '#dc2626',
      background: isDarkMode ? '#1e293b' : '#fff',
      color: isDarkMode ? '#e2e8f0' : '#1f2937'
    });
  };

  // Global Stats Calculation
  const globalStats = useMemo(() => {
    let stats = {
      totalItems: allInventoryData.length,
      babyRiskCount: 0,
      expiringCount: 0,
      activeInventoryValue: 0,
      rescuedValue: 0,
      totalValue: 0
    };

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    allInventoryData.forEach(item => {
      // Kitchen value cards must follow the stored batch price logic shown in MyKitchen.
      // [Tutarlılık] TopBar ile birebir aynı kural: kurtarılan ürün (is_rescued) veya negatif değer
      // → Kurtarılan; aksi halde aktif envanter değeri. Önceden sadece val<0 sayılıyordu, bu yüzden
      // pozitif purchase_price'lı kurtarma batch'leri (örn. domates→sos 119₺) panelde 0 görünüyordu.
      const val = calculateBatchValue(item);
      if (item.is_rescued === true || val < 0) {
        if (!isNaN(val)) stats.rescuedValue += Math.abs(val);
      } else if (!isNaN(val)) {
        stats.activeInventoryValue += val;
      }

      // Expiry Calculation (Items expiring within 3 days or expired)
      if (item.expiry_date) {
        const exp = new Date(item.expiry_date);
        exp.setHours(0, 0, 0, 0);
        const diffDays = Math.ceil((exp - today) / (1000 * 60 * 60 * 24));
        // Count if expired or expiring soon (<= 3 days)
        if (diffDays <= 3) stats.expiringCount++;
      }

      // Baby Risk Calculation
      if (checkIsBabyRisky(item)) {
        stats.babyRiskCount++;
      }
    });

    stats.totalValue = stats.activeInventoryValue + stats.rescuedValue;
    return stats;
  }, [allInventoryData, hasChildren]);

  // Smart Search Logic (Modernized V21.0)
  const handleInventorySearch = async (preferredCategoriesInput = ['main', 'soup', 'salad'], options = {}) => {
    // Defense against React event objects
    const preferredCategories = Array.isArray(preferredCategoriesInput)
      ? preferredCategoriesInput
      : ['main', 'soup', 'salad'];

    setSmartSearchLoading(true);
    setSmartSearchModalOpen(true);
    try {
      const response = await fetch(`${API_BASE}/api/smart-search/recommendations`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          preferredCategories,
          servingSize: options.servingSize || smartPortion,
          skillLevel: options.skillLevel || smartSkillLevel
        })
      });
      const data = await response.json();
      if (data.success) {
        setSmartSearchRecipes(data.recipes || []);
        setSmartStructuredMenu(data.structuredMenu || null);
        setAvailableComponents(data.availableComponents || []);
      } else {
        Swal.fire('Hata', data.message || 'Öneri alınamadı', 'error');
      }
    } catch (err) {
      console.error('Smart search error:', err);
      Swal.fire('Hata', 'Sunucu bağlantı hatası', 'error');
    } finally {
      setSmartSearchLoading(false);
    }
  };

  const handleDirectSearchToSmartSearch = () => {
    handleInventorySearch(['main', 'soup', 'salad'], { source: 'mykitchen_direct_search' });
  };

  const handleSmartRecipeClick = async (recipe) => {
    const pantryIngredients = (allInventoryData || [])
      .map((item) => item?.ingredient_name || item?.ingredient?.name || item?.name || '')
      .filter(Boolean);

    const hydrated = await hydrateRecipeForModal(recipe, {
      forceFullFetch: true,
      skipFullFetch: false,
      pantryIngredients
    });

    const selectedRecipe = hydrated || recipe;
    setSelectedSmartRecipe(selectedRecipe);
    setSmartRecipeModalOpen(true);

    const selectedRecipeId = getRecipeBehaviorEntityId(selectedRecipe);
    const selectedMeta = getDecisionChoiceMeta(selectedRecipeId);
    if (selectedRecipeId && selectedMeta?.recommendationId) {
      const shownAt = shownAtByRecommendationRef.current.get(selectedMeta.recommendationId);
      const timeToAcceptSeconds = Number.isFinite(shownAt)
        ? (Date.now() - Number(shownAt)) / 1000
        : undefined;

      trackRecommendationAccepted({
        source: 'kitchen_behavior_engine',
        surface: 'my_kitchen',
        context: 'decision_select',
        metadata: {
          recommendationId: selectedMeta.recommendationId,
          recipeId: selectedRecipeId,
          rank: selectedMeta.rank,
          timeToAcceptSeconds
        }
      });

      if (Number.isFinite(timeToAcceptSeconds) && timeToAcceptSeconds >= 0 && timeToAcceptSeconds < 10) {
        trackDecisionWow({
          source: 'kitchen_behavior_engine',
          surface: 'my_kitchen',
          context: 'decision_select',
          metadata: {
            recommendationId: selectedMeta.recommendationId,
            recipeId: selectedRecipeId,
            timeToAcceptSeconds
          }
        });
      }
    }

    const primaryRecipeId = behaviorDecisionSet?.primaryChoice?.recipeId
      ? String(behaviorDecisionSet.primaryChoice.recipeId)
      : null;
    if (selectedRecipeId && primaryRecipeId && selectedRecipeId !== primaryRecipeId) {
      const primaryMeta = getDecisionChoiceMeta(primaryRecipeId);
      if (primaryMeta?.recommendationId && !skippedRecommendationRef.current.has(primaryMeta.recommendationId)) {
        skippedRecommendationRef.current.add(primaryMeta.recommendationId);
        trackRecommendationSkipped({
          source: 'kitchen_behavior_engine',
          surface: 'my_kitchen',
          context: 'primary_bypassed',
          metadata: {
            recommendationId: primaryMeta.recommendationId,
            recipeId: primaryRecipeId,
            skipReason: 'not_interested'
          }
        });
        trackRecipeRejectedReason({
          source: 'kitchen_behavior_engine',
          surface: 'my_kitchen',
          context: 'primary_bypassed',
          metadata: {
            recommendationId: primaryMeta.recommendationId,
            recipeId: primaryRecipeId,
            reasonCode: 'not_now'
          }
        });
      }
    }

    if (selectedRecipeId && primaryRecipeId && selectedRecipeId === primaryRecipeId) {
      postDecisionFeedback('accepted', selectedRecipeId);
    }
  };

  const handleRecipeUsedSuccess = (recipe) => {
    const usedRecipeId = getRecipeBehaviorEntityId(recipe);
    const primaryRecipeId = behaviorDecisionSet?.primaryChoice?.recipeId
      ? String(behaviorDecisionSet.primaryChoice.recipeId)
      : null;
    if (usedRecipeId && primaryRecipeId && usedRecipeId === primaryRecipeId) {
      postDecisionFeedback('completed', usedRecipeId);
    }
  };

  return (
    <div className="w-full">
      {/* Mobile Full Screen Layout */}
      <div className="sm:hidden bg-gray-50 dark:bg-slate-950 min-h-screen pb-4 text-[12px]">
        <div className="px-2.5 pt-1 flex flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] text-gray-500 dark:text-gray-400">Envanter</p>
              <div className="flex items-center gap-3">
                <h1 className="text-[15px] font-bold leading-tight text-gray-900 dark:text-white">Mutfağım</h1>
                <button
                  onClick={openStapleModal}
                  className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 px-2.5 py-1 rounded-lg border border-red-100 dark:border-red-800 text-[10px] font-bold flex items-center gap-1.5 shadow-sm active:scale-95 transition-all relative"
                >
                  <div className="absolute -top-1 -right-1 size-3 bg-red-600 text-white text-[8px] flex items-center justify-center rounded-full animate-pulse border border-white dark:border-slate-900">
                    !
                  </div>
                  <span className="material-symbols-outlined text-[14px]">checklist</span>
                  Varsayılan
                </button>
              </div>
            </div>
          </div>

          {/* Mobile Global Dashboard - 3 Column Grid */}
          <div className="grid grid-cols-3 gap-1.5 p-2 bg-white dark:bg-slate-900 rounded-lg border border-gray-100 dark:border-slate-800 shadow-sm">
            <div className="flex flex-col items-center justify-center p-1.5 text-center bg-blue-50/30 dark:bg-blue-900/10 rounded-lg">
              <span className="text-[8px] text-gray-500 dark:text-gray-400 font-bold mb-0.5 whitespace-nowrap uppercase">Toplam</span>
              <div className="text-blue-600 dark:text-blue-400 font-bold text-sm leading-none">{globalStats.totalItems}</div>
            </div>
            {(() => {
              const babyCount = globalStats.babyRiskCount;

              // Calculate Allergen Risks on the fly for the dashboard
              const allergenCount = allInventoryData.reduce((acc, item) => {
                const nutrition = item.ingredient?.nutrition_data || {};
                const userAllergies = [
                  ...(userProfile?.allergies || []),
                  ...(userProfile?.dietaryRestrictions || []),
                  ...(userProfile?.intakeSurvey?.healthConditions || [])
                ].map(a => a.toLowerCase());

                // 1. Check Nutrition Allergens
                const nutritionAllergens = Array.isArray(nutrition.allergens) ? nutrition.allergens : (nutrition.allergen_group && nutrition.allergen_group !== 'none' ? [nutrition.allergen_group] : []);
                const isNutritionRisky = nutritionAllergens.some(groupKey => {
                  if (groupKey === 'none') return false;
                  return userAllergies.some(a =>
                    a === groupKey.toLowerCase() ||
                    (groupKey === 'gluten' && a.includes('gluten')) ||
                    (groupKey === 'nuts' && (a.includes('fındık') || a.includes('fıstık'))) ||
                    (groupKey === 'dairy' && (a.includes('süt') || a.includes('laktor')))
                  );
                });

                // 2. Check Brand Allergens
                const brandRisksRaw = item.brand_allergen_risks || item.brandAllergenRisks || [];
                const brandRisks = Array.isArray(brandRisksRaw) ? brandRisksRaw : (typeof brandRisksRaw === 'string' ? JSON.parse(brandRisksRaw) : []);
                const isBrandRisky = brandRisks.some(risk =>
                  userAllergies.some(a => a.includes(risk.toLowerCase()) || risk.toLowerCase().includes(a))
                );

                return acc + ((isNutritionRisky || isBrandRisky) ? 1 : 0);
              }, 0);

              const hasBabyRisk = babyCount > 0;
              const hasAllergenRisk = allergenCount > 0;

              if (!hasBabyRisk && !hasAllergenRisk) {
                return (
                  <div className="flex flex-col items-center justify-center p-1.5 text-center bg-green-50/20 dark:bg-green-900/10 rounded-lg border border-transparent shadow-inner opacity-60">
                    <div className="flex items-center gap-1 text-green-500 justify-center">
                      <span className="material-symbols-outlined text-[14px]">check_circle</span>
                    </div>
                    <span className="text-[8px] text-green-500 font-bold whitespace-nowrap uppercase mt-0.5">Güvenli</span>
                  </div>
                );
              }

              return (
                <div className="flex flex-col h-full rounded-lg overflow-hidden border border-gray-100 dark:border-slate-800">
                  {/* Top Half: Baby Risk (if exists) */}
                  {hasBabyRisk && (
                    <div
                      className={`flex-1 flex flex-col items-center justify-center p-0.5 transition-colors cursor-pointer ${filterMode === 'baby' ? 'bg-orange-100 dark:bg-orange-900/40' : 'bg-orange-50 dark:bg-orange-900/20'} ${!hasAllergenRisk ? 'rounded-lg' : 'rounded-t-lg border-b border-orange-200 dark:border-orange-900/30'}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        const newMode = filterMode === 'baby' ? 'zone' : 'baby';
                        setFilterMode(newMode);
                        if (newMode === 'baby') {
                          setActiveZone('Fridge');
                          setActiveSubZone('Tümü');
                          setTimeout(() => {
                            document.getElementById('inventory-list-header')?.scrollIntoView({ behavior: 'smooth' });
                          }, 100);
                        }
                      }}
                    >
                      <div className="flex items-center gap-1 text-orange-600 dark:text-orange-400">
                        <span className="text-[8px] font-bold uppercase">BEBEK</span>
                        <span className="text-xs font-black">{babyCount}</span>
                      </div>
                    </div>
                  )}

                  {/* Bottom Half: Allergen Risk (if exists) */}
                  {hasAllergenRisk && (
                    <div
                      className={`flex-1 flex flex-col items-center justify-center p-0.5 transition-colors cursor-pointer ${filterMode === 'allergen' ? 'bg-red-100 dark:bg-red-900/40' : 'bg-red-50 dark:bg-red-900/20'} ${!hasBabyRisk ? 'rounded-lg' : 'rounded-b-lg'}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        const newMode = filterMode === 'allergen' ? 'zone' : 'allergen';
                        setFilterMode(newMode);
                        if (newMode === 'allergen') {
                          setActiveZone('Fridge');
                          setActiveSubZone('Tümü');
                          setTimeout(() => {
                            document.getElementById('inventory-list-header')?.scrollIntoView({ behavior: 'smooth' });
                          }, 100);
                        }
                      }}
                    >
                      <div className="flex items-center gap-1 text-red-600 dark:text-red-400">
                        <span className="text-[8px] font-bold uppercase">ALERJEN</span>
                        <span className="text-xs font-black">{allergenCount}</span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
            <div
              className={`flex flex-col items-center justify-center p-1.5 text-center rounded-lg transition-colors border relative ${filterMode === 'expiring' ? 'bg-red-50 dark:bg-red-900/30 border-red-200' : 'bg-red-50/20 dark:bg-red-900/5 border-transparent'}`}
              onClick={() => {
                const newMode = filterMode === 'expiring' ? 'zone' : 'expiring';
                setFilterMode(newMode);
                if (newMode === 'expiring') {
                  setActiveZone('Fridge');
                  setActiveSubZone('Tümü');
                  setTimeout(() => {
                    document.getElementById('inventory-list-header')?.scrollIntoView({ behavior: 'smooth' });
                  }, 100);
                }
              }}
            >
              {globalStats.expiringCount > 0 && (
                <div className="absolute -top-1 -right-1 size-3.5 bg-red-500 rounded-full flex items-center justify-center animate-pulse border-2 border-white dark:border-slate-900 shadow-sm z-10">
                  <span className="text-[9px] text-white font-black leading-none">!</span>
                </div>
              )}
              <span className="text-[8px] text-red-500 dark:text-red-500 font-bold mb-0.5 whitespace-nowrap uppercase">SKT</span>
              <div className="text-red-600 dark:text-red-400 font-bold text-sm leading-none">{globalStats.expiringCount}</div>
            </div>
            <div
              className="flex flex-col items-center justify-center p-1.5 text-center bg-blue-50/20 dark:bg-blue-900/5 rounded-lg border border-blue-100/50 dark:border-blue-900/20 cursor-pointer active:scale-95 transition-all relative"
              onClick={() => setIsValueModalOpen(true)}
            >
              <div className="absolute -top-1 -right-1 size-3.5 bg-blue-500 rounded-full flex items-center justify-center animate-pulse border-2 border-white dark:border-slate-900 shadow-sm z-10">
                <span className="text-[9px] text-white font-black leading-none">!</span>
              </div>
              <span className="text-[8px] text-blue-500 dark:text-blue-500 font-bold mb-0.5 whitespace-nowrap uppercase">Değer</span>
              <div className="text-blue-600 dark:text-blue-400 font-bold text-xs leading-none">{formatCurrency(globalStats.totalValue)}</div>
            </div>
            <div
              className={`flex flex-col items-center justify-center p-1.5 text-center rounded-lg border cursor-pointer active:scale-95 transition-all relative ${filterMode === 'rescued' ? 'bg-green-50 dark:bg-green-900/30 border-green-200' : 'bg-green-50/20 dark:bg-green-900/5 border-green-100/50 dark:border-green-900/20'}`}
              onClick={() => {
                const newMode = filterMode === 'rescued' ? 'zone' : 'rescued';
                setFilterMode(newMode);
                if (newMode === 'rescued') {
                  setActiveZone('Fridge');
                  setActiveSubZone('Tümü');
                  setTimeout(() => {
                    document.getElementById('inventory-list-header')?.scrollIntoView({ behavior: 'smooth' });
                  }, 100);
                }
              }}
            >
              <div className="absolute -top-1 -right-1 size-3.5 bg-green-500 rounded-full flex items-center justify-center animate-pulse border-2 border-white dark:border-slate-900 shadow-sm z-10">
                <span className="text-[9px] text-white font-black leading-none">!</span>
              </div>
              <span className="text-[8px] text-green-600 dark:text-green-500 font-bold mb-0.5 whitespace-nowrap uppercase">Kurtarılan</span>
              <div className="text-green-600 dark:text-green-400 font-bold text-xs leading-none">+{formatCurrency(globalStats.rescuedValue)}</div>
            </div>
            <div
              className="group flex flex-col items-center justify-center p-1.5 text-center bg-red-50/50 dark:bg-red-900/20 rounded-lg border border-red-100 dark:border-red-900/30 cursor-pointer active:scale-95 transition-all relative"
              onClick={openWasteHistory}
            >
              <div className="absolute -top-1 -right-1 size-3.5 bg-red-600 rounded-full flex items-center justify-center animate-pulse border-2 border-white dark:border-slate-900 shadow-sm z-10">
                <span className="text-[9px] text-white font-black leading-none">!</span>
              </div>
              <span className="text-[8px] text-red-600 dark:text-red-400 font-black mb-0.5 whitespace-nowrap uppercase">İsraf Olan</span>
              <div className="text-red-600 dark:text-red-400 font-bold text-xs leading-none">-{formatCurrency(wastedStats.totalWasted)}</div>
            </div>
          </div>

          <div className="bg-white dark:bg-slate-900 rounded-lg border border-gray-200 dark:border-slate-800 shadow-sm p-1.5">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleDirectSearchToSmartSearch}
                className="text-red-500 hover:text-red-600 transition-colors"
                aria-label="Smart Search aç"
                title="Smart Search aç"
              >
                <span className="material-symbols-outlined text-[20px]">search</span>
              </button>
              <input
                className="flex-1 bg-transparent text-gray-800 dark:text-white placeholder-gray-400 text-[12px] focus:outline-none"
                placeholder="Ürün veya marka ara"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleDirectSearchToSmartSearch();
                  }
                }}
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="text-gray-500 hover:text-gray-700 active:scale-95 transition-transform"
                  aria-label="Aramayı temizle"
                >
                  <span className="material-symbols-outlined text-[20px]">close</span>
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="px-2.5 pt-2 flex flex-col gap-2.5">
          {/* Onboarding Smart Kitchen Banner */}
          {allInventoryData.some(item => item.originType === 'ONBOARDING_SEED') && (
            <div className="bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 rounded-xl p-3 flex items-start gap-3 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-16 h-16 bg-primary/10 rounded-full blur-xl -translate-y-1/2 translate-x-1/2"></div>
              <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                <i className="fa-solid fa-wand-magic-sparkles text-primary text-sm"></i>
              </div>
              <div>
                <h4 className="text-[13px] font-bold text-gray-900 dark:text-white mb-0.5">Akıllı Mutfağınız Aktif</h4>
                <p className="text-[11px] text-gray-600 dark:text-gray-400 leading-tight">
                  Sizin için rutininize uygun başlangıç malzemeleri seçtik. Dilediğiniz ürünü silip, yenisini ekleyebilirsiniz.
                </p>
              </div>
            </div>
          )}

          {/* Mobile Zone Navigation */}
          <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-100 dark:border-slate-800 p-1.5">
            <div className="grid grid-cols-2 gap-1.5">
              {Object.entries(zones).map(([zoneKey, zone]) => (
                <button
                  key={zoneKey}
                  onClick={() => {
                    setActiveZone(zoneKey);
                    if (zone.subZones.length > 0) {
                      setActiveSubZone(zone.subZones[0]);
                    }
                    setFilterMode('zone');
                  }}
                  className={`flex flex-col items-start gap-1.5 p-2 rounded-lg border text-left transition-all shadow-sm ${activeZone === zoneKey
                    ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 shadow-md shadow-red-100 dark:shadow-none'
                    : 'bg-gray-50 dark:bg-slate-800 border-gray-200 dark:border-slate-700 text-gray-800 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'
                    }`}
                  style={{ minWidth: 0 }}
                >
                  <span className={`material-symbols-outlined text-[18px] ${activeZone === zoneKey ? 'text-red-600 dark:text-red-400' : 'text-gray-600 dark:text-slate-400'}`}>{zone.icon}</span>
                  <div>
                    <p className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 leading-none">Bölge</p>
                    <p className={`text-[13px] font-bold leading-tight ${activeZone === zoneKey ? 'text-red-700 dark:text-red-400' : 'text-gray-900 dark:text-white'}`}>{zone.name.replace('\n', ' ')}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Mobile SubZone Navigation */}
          <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-100 dark:border-slate-800 p-1.5">
            <div className="grid grid-cols-3 gap-1.5">
              {zones[activeZone]?.subZones.map((subZone) => (
                <button
                  key={subZone}
                  onClick={() => {
                    console.log('📱 Mobile SubZone clicked:', { activeZone, oldSubZone: activeSubZone, newSubZone: subZone });
                    setActiveSubZone(subZone);
                  }}
                  className={`px-1 py-2 rounded-lg font-bold text-[11px] text-center transition-all border break-words leading-tight ${activeSubZone === subZone
                    ? 'bg-red-600 text-white border-red-600 shadow-md shadow-red-200 dark:shadow-none'
                    : 'bg-slate-50 dark:bg-slate-800 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-slate-700 hover:bg-gray-100 dark:hover:bg-slate-700'
                    }`}
                >
                  {subZone}
                </button>
              ))}
            </div>
          </div>

          {/* Mobile Content */}
          {favoriteError ? (
            <div className="mx-3 mb-2 rounded-lg border border-rose-200 dark:border-rose-900/40 bg-rose-50 dark:bg-rose-950/20 px-3 py-2 text-xs text-rose-700 dark:text-rose-300">
              Favori islemi tamamlanamadi. Lutfen tekrar deneyin.
            </div>
          ) : null}
          <div className="pb-3">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-3 border-red-600 mx-auto"></div>
                  <p className="mt-3 text-base text-gray-600">Yükleniyor...</p>
                </div>
              </div>
            ) : error ? (
              <div className="flex items-center justify-center py-16">
                <div className="text-center">
                  <span className="material-symbols-outlined text-[72px] text-gray-300 dark:text-slate-700 block mb-3">error</span>
                  <p className="text-base text-gray-600 dark:text-gray-400">{error}</p>
                </div>
              </div>
            ) : filteredInventory.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center gap-4">
                <span className="material-symbols-outlined text-[68px] text-gray-300 dark:text-slate-700 block">inventory</span>
                <div>
                  <p className="text-lg font-semibold text-gray-800 dark:text-white">Henüz ürün yok</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Yeni bir ürün ekleyerek başlayabilirsin.</p>
                </div>
                <button
                  onClick={openAddModal}
                  className="flex items-center gap-2 bg-red-600 text-white px-6 py-3 rounded-2xl font-bold text-sm shadow-lg shadow-red-500/30 active:scale-95 transition-transform"
                >
                  <span className="material-symbols-outlined text-[22px]">add</span>
                  Ürün Ekle
                </button>
              </div>
            ) : (
              <div className="grid gap-1.75">
                {filteredInventory.map((item) => {
                  const expiryStatus = getExpiryStatus(item.expiry_date);
                  const babySafetyBadge = getBabySafetyBadge(item);
                  const allergenBadge = getAllergenBadge(item);
                  const lowerItemName = (item.ingredient_name || item.ingredient?.name || '').toLowerCase();

                  // Check for risks for MOBILE CARD highlighting
                  const userAllergies = [
                    ...(userProfile?.allergies || []),
                    ...(userProfile?.dietaryRestrictions || []),
                    ...(userProfile?.intakeSurvey?.healthConditions || [])
                  ].map(a => a.toLowerCase());

                  const nutrition = item.ingredient?.nutrition_data || {};
                  const allergens = Array.isArray(nutrition.allergens) ? nutrition.allergens : (nutrition.allergen_group && nutrition.allergen_group !== 'none' ? [nutrition.allergen_group] : []);

                  const isAllergenRowRisky = allergens.some(groupKey => {
                    if (groupKey === 'none') return false;
                    const allergenName = groupKey;
                    const isAllergenMatch = userAllergies.some(a =>
                      a === groupKey.toLowerCase() ||
                      (groupKey === 'gluten' && a.includes('gluten')) ||
                      (groupKey === 'nuts' && (a.includes('fındık') || a.includes('fıstık'))) ||
                      (groupKey === 'dairy' && (a.includes('süt') || a.includes('laktor')))
                    );
                    return isAllergenMatch;
                  }) || (lowerItemName.includes('bal') && !lowerItemName.includes('balık') && !lowerItemName.includes('kabak') && userAllergies.some(a => a.includes('bal')));

                  const isBabyRowRisky = checkIsBabyRisky(item);

                  const brandRisksRaw = item.brand_allergen_risks || item.brandAllergenRisks || [];
                  const brandRisks = Array.isArray(brandRisksRaw)
                    ? brandRisksRaw
                    : (typeof brandRisksRaw === 'string' ? JSON.parse(brandRisksRaw) : []);

                  const isBrandRisky = brandRisks.some(risk =>
                    userAllergies.some(a =>
                      a.toLowerCase() === risk.toLowerCase() ||
                      (risk.toLowerCase() === 'gluten' && a.toLowerCase().includes('gluten'))
                    )
                  );

                  return (
                    <div
                      key={item.id}
                      className={`bg-white dark:bg-slate-900 rounded-md p-2.5 shadow-[0_5px_16px_-12px_rgba(0,0,0,0.18)] border relative overflow-hidden transition-colors
                        ${(isAllergenRowRisky || isBrandRisky || isBabyRowRisky)
                          ? 'border-red-600 border-2 bg-red-50 dark:bg-red-900/20'
                          : 'border-gray-100 dark:border-slate-800'}`}
                    >
                      <div className={`absolute inset-0 pointer-events-none ${(isAllergenRowRisky || isBrandRisky) ? 'bg-red-50/50 dark:bg-red-900/20' : 'bg-gradient-to-br from-red-50 dark:from-red-950/20 via-transparent to-white dark:to-slate-900'}`} aria-hidden="true"></div>
                      <div className="relative flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1">{item.storage_location || 'Depo'}</p>
                          <div className="flex flex-col gap-1.5">
                            <h3 className="text-[15px] font-bold text-gray-900 dark:text-white leading-tight break-words flex items-center gap-1.5">
                              {isBrandRisky && (
                                <span
                                  className="material-symbols-outlined text-red-600 animate-pulse fill-1 cursor-help"
                                  title={`⚠️ DİKKAT: Bu MARKADA şu riskler var: ${brandRisks.join(', ')}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    Swal.fire({
                                      title: 'Marka Riski!',
                                      text: `Bu markanın içeriğinde şu alerjenler bulunabilir: ${brandRisks.join(', ')}.\nLütfen tüketmeden önce paketi kontrol edin.`,
                                      icon: 'warning',
                                      confirmButtonText: 'Anladım',
                                      confirmButtonColor: '#dc2626'
                                    });
                                  }}
                                >
                                  flag
                                </span>
                              )}
                              {item.ingredient_name || 'Unknown Item'}
                              <div className="flex items-center gap-1 ml-1">
                                {babySafetyBadge}
                                {allergenBadge}
                              </div>
                              {(isAllergenRowRisky || isBrandRisky) && <span className="ml-1 text-[9px] text-red-600 font-extrabold tracking-wider animate-pulse uppercase">(!RİSK)</span>}
                            </h3>
                            <div className={`self-start text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-md ${item.brand ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-800/50' : 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800/50'}`}>
                                {item.brand || 'Akıllı Stok'}
                            </div>
                          </div>
                        </div>

                        <div
                          className={`flex flex-col items-center px-2.5 py-1.5 rounded-lg bg-${expiryStatus.color}-50 dark:bg-${expiryStatus.color}-950/20 border border-${expiryStatus.color}-100 dark:border-${expiryStatus.color}-900/50 min-w-[80px] cursor-help`}
                          title={expiryStatus.tooltip}
                        >
                          <span className={`material-symbols-outlined text-[20px] text-${expiryStatus.color}-600 dark:text-${expiryStatus.color}-400 fill-1`}>{expiryStatus.icon}</span>
                          <span className={`text-[10px] font-bold text-${expiryStatus.color}-700 dark:text-${expiryStatus.color}-300 text-center leading-tight mt-1`}>{expiryStatus.text}</span>
                        </div>
                      </div>

                      <div className="relative mt-3 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-2xl font-bold text-gray-900 dark:text-white leading-none">
                            {formatDecimal(item.display_quantity ?? item.remaining_amount)}
                          </span>
                          <span className="px-2.5 py-1 rounded-lg bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-gray-300 font-semibold text-[12px]">{item.display_unit || item.unit}</span>
                        </div>
                        {(() => {
                          const val = calculateBatchValue(item);
                          const isNegative = val < 0;
                          return (item.purchase_price || val !== 0) && (
                            <div className="flex flex-col items-end">
                              <span className={`text-base font-bold ${isNegative ? 'text-green-600' : 'text-gray-900 dark:text-white'}`}>
                                {formatCurrency(val)}
                              </span>
                              <span className="text-[10px] text-gray-400 opacity-60">
                                {isNegative ? 'Tasarruf Değeri' : 'Stok Değeri'}
                              </span>
                            </div>
                          );
                        })()}
                      </div>

                      <div className="relative mt-2.5 flex flex-wrap items-center gap-1.5">
                        {(item.can_be_opened || item.is_opened) && (
                          item.is_opened ? (
                            <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-orange-100 dark:bg-orange-950/30 text-orange-700 dark:text-orange-400 border border-orange-200 dark:border-orange-900/50">
                              <span className="material-symbols-outlined text-[17px] fill-1">package_2</span>
                              <span className="text-[11px] font-bold">Açık</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-green-100 dark:bg-green-950/30 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-900/50">
                                <span className="material-symbols-outlined text-[17px] fill-1">inventory_2</span>
                                <span className="text-[11px] font-bold">Kapalı</span>
                              </div>
                              <button
                                onClick={() => confirmOpenItem(item)}
                                title={item.open_action_reason || 'Bu ürünü açtım olarak işaretle'}
                                className="px-2.5 py-1.25 rounded-lg bg-blue-600 text-white text-[11px] font-bold active:scale-95 transition-transform"
                              >
                                Açıldı
                              </button>
                            </div>
                          )
                        )}

                        {babySafetyBadge}
                        {allergenBadge}
                      </div>

                      <div className="relative mt-3 grid grid-cols-4 gap-2">
                        <button
                          onClick={() => toggleInventoryFavorite(item)}
                          disabled={favoriteBusyEntityId === getInventoryFavoriteEntityId(item)}
                          className={`flex items-center justify-center gap-1.5 py-2.5 rounded-lg font-bold text-[12px] active:scale-95 transition-transform disabled:opacity-60 ${
                            isInventoryFavorite(item)
                              ? 'bg-rose-50 dark:bg-rose-950/20 text-rose-700 dark:text-rose-300 hover:bg-rose-100 dark:hover:bg-rose-900/30'
                              : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
                          }`}
                        >
                          <span className="material-symbols-outlined text-[18px]" style={{ fontVariationSettings: isInventoryFavorite(item) ? "'FILL' 1" : "'FILL' 0" }}>favorite</span>
                          Favori
                        </button>
                        <button
                          onClick={() => handleQuickManualConsume(item)}
                          className="flex items-center justify-center gap-1.5 py-2.5 rounded-lg bg-emerald-50 dark:bg-emerald-950/20 text-emerald-700 dark:text-emerald-300 font-bold text-[12px] hover:bg-emerald-100 dark:hover:bg-emerald-900/30 active:scale-95 transition-transform"
                        >
                          <span className="material-symbols-outlined text-[18px]">restaurant</span>
                          Yedim
                        </button>
                        <button
                          onClick={() => openEditModal(item)}
                          className="flex items-center justify-center gap-1.5 py-2.5 rounded-lg bg-gray-100 dark:bg-slate-800 text-gray-800 dark:text-gray-300 font-bold text-[12px] hover:bg-gray-200 dark:hover:bg-slate-700 active:scale-95 transition-transform"
                        >
                          <span className="material-symbols-outlined text-[18px]">edit</span>
                          Düzenle
                        </button>
                        <button
                          onClick={() => openDeleteModal(item)}
                          className="flex items-center justify-center gap-1.5 py-2.5 rounded-lg bg-red-50 dark:bg-red-950/20 text-red-700 dark:text-red-400 font-bold text-[12px] hover:bg-red-100 dark:hover:bg-red-900/40 active:scale-95 transition-transform"
                        >
                          <span className="material-symbols-outlined text-[18px]">delete</span>
                          Sil
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Desktop Layout */}
      {/* Desktop Layout - Full Page Scroll */}
      <div className="hidden sm:flex w-full bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 relative shadow-sm rounded-2xl overflow-hidden">

        {/* Zone Sidebar - Sticky */}
        <aside className="hidden sm:flex w-[80px] bg-white dark:bg-slate-900 border-r border-gray-200 dark:border-slate-800 flex-col items-center py-5 shrink-0 z-20 sticky top-0 self-stretch">
          <div className="h-2"></div>
          <nav className="flex flex-col w-full gap-3 mt-1">
            {Object.entries(zones).map(([zoneKey, zone]) => (
              <button
                key={zoneKey}
                onClick={() => {
                  console.log('🏢 Zone clicked:', { oldZone: activeZone, newZone: zoneKey, newSubZone: zone.subZones[0] });
                  setActiveZone(zoneKey);
                  setActiveSubZone(zone.subZones[0]);
                  setFilterMode('zone'); // Reset filter to zone view
                }}
                className={`group w-full flex flex-col items-center gap-1 p-2 transition-all relative ${activeZone === zoneKey ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400'
                  }`}
              >
                {activeZone === zoneKey && (
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-6 bg-red-600 rounded-r-full"></div>
                )}
                <div className={`p-2.5 rounded-xl shadow-sm ring-1 transition-colors duration-200 ${activeZone === zoneKey
                  ? 'bg-red-50 dark:bg-red-950/20 text-red-600 dark:text-red-400 ring-red-100 dark:ring-red-900/20'
                  : 'hover:bg-red-50 dark:hover:bg-slate-800 ring-gray-100 dark:ring-slate-800'
                  }`}>
                  <span className="material-symbols-outlined text-[24px] fill-1">{zone.icon}</span>
                </div>
                <span className="text-[9px] font-bold tracking-wide text-center leading-tight whitespace-pre-line dark:text-slate-300">
                  {zone.name}
                </span>
              </button>
            ))}
          </nav>
          <div className="mt-auto">
            <button className="size-9 flex items-center justify-center rounded-full bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors border border-gray-200 dark:border-slate-700">
              <span className="material-symbols-outlined text-[20px]">settings</span>
            </button>
          </div>
        </aside>



        {/* Main Content Area */}
        <div className="flex-1 flex flex-col relative bg-gray-50 dark:bg-slate-950">
          {/* Desktop Header */}
          <header className="bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-slate-800 shrink-0 z-10 px-2 md:px-10 pt-2 md:pt-6 pb-0 flex flex-col">
            <div className="mb-4 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">Mutfağım</h1>
                  <button
                    onClick={openStapleModal}
                    className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 px-3 py-1.5 rounded-xl border border-red-100 dark:border-red-800 text-xs font-bold flex items-center gap-1.5 shadow-sm hover:shadow-md transition-all relative group"
                  >
                    <div className="absolute -top-1 -right-1 size-3.5 bg-red-600 text-white text-[10px] flex items-center justify-center rounded-full animate-pulse border-2 border-white dark:border-slate-900 z-10">
                      !
                    </div>
                    <span className="material-symbols-outlined text-[18px] group-hover:rotate-12 transition-transform">checklist</span>
                    Varsayılan Listesi
                  </button>
                </div>
              </div>

              {/* Global Dashboard */}
              <div className="grid grid-cols-6 gap-4 p-4 bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm transition-all duration-300">
                {/* 1. Total Items */}
                <div className="flex flex-col items-center justify-center p-2 text-center border-r border-gray-100 dark:border-slate-700 last:border-0">
                  <span className="text-xs text-gray-500 dark:text-gray-400 font-semibold mb-1">TOPLAM ÜRÜN</span>
                  <div className="flex items-center gap-1.5 text-blue-600 dark:text-blue-400">
                    <span className="material-symbols-outlined text-[20px]">inventory_2</span>
                    <span className="text-xl font-bold tracking-tight">{globalStats.totalItems}</span>
                  </div>
                </div>

                {/* 2. SPLIT RISK CARD: Baby & Allergen */}
                {(() => {
                  const babyCount = globalStats.babyRiskCount;

                  // Calculate Allergen Risks on the fly for the dashboard
                  // (Ideally this should be in globalStats useMemo but we can calculate here for now to avoid massive refactor)
                  const allergenCount = allInventoryData.reduce((acc, item) => {
                    const nutrition = item.ingredient?.nutrition_data || {};
                    const userAllergies = [
                      ...(userProfile?.allergies || []),
                      ...(userProfile?.dietaryRestrictions || []),
                      ...(userProfile?.intakeSurvey?.healthConditions || [])
                    ].map(a => a.toLowerCase());

                    // 1. Check Nutrition Allergens
                    const nutritionAllergens = Array.isArray(nutrition.allergens) ? nutrition.allergens : (nutrition.allergen_group && nutrition.allergen_group !== 'none' ? [nutrition.allergen_group] : []);
                    const isNutritionRisky = nutritionAllergens.some(groupKey => {
                      if (groupKey === 'none') return false;
                      return userAllergies.some(a =>
                        a === groupKey.toLowerCase() ||
                        (groupKey === 'gluten' && a.includes('gluten')) ||
                        (groupKey === 'nuts' && (a.includes('fındık') || a.includes('fıstık'))) ||
                        (groupKey === 'dairy' && (a.includes('süt') || a.includes('laktor')))
                      );
                    });

                    // 2. Check Brand Allergens
                    const brandRisksRaw = item.brand_allergen_risks || item.brandAllergenRisks || [];
                    const brandRisks = Array.isArray(brandRisksRaw) ? brandRisksRaw : (typeof brandRisksRaw === 'string' ? JSON.parse(brandRisksRaw) : []);
                    const isBrandRisky = brandRisks.some(risk =>
                      userAllergies.some(a => a.includes(risk.toLowerCase()) || risk.toLowerCase().includes(a))
                    );

                    return acc + ((isNutritionRisky || isBrandRisky) ? 1 : 0);
                  }, 0);

                  const hasBabyRisk = babyCount > 0;
                  const hasAllergenRisk = allergenCount > 0;

                  // If NO risks at all, show empty placeholder or safe state? 
                  // Original design just showed 0 baby risk. Let's default to standard "Safe" if 0.
                  if (!hasBabyRisk && !hasAllergenRisk) {
                    return (
                      <div className="flex flex-col items-center justify-center p-2 text-center border-r border-gray-100 dark:border-slate-700 last:border-0 rounded-lg bg-green-50/50 dark:bg-green-900/10">
                        <span className="text-xs text-gray-400 dark:text-gray-500 font-semibold mb-1">RİSK DURUMU</span>
                        <div className="flex items-center gap-1.5 text-green-500">
                          <span className="material-symbols-outlined text-[20px]">check_circle</span>
                          <span className="text-sm font-bold">GÜVENLİ</span>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div className="flex flex-col border-r border-gray-100 dark:border-slate-700 last:border-0 h-full">
                      {/* Top Half: Baby Risk (if exists) */}
                      {hasBabyRisk && (
                        <div
                          className={`flex-1 flex items-center justify-center gap-2 cursor-pointer transition-colors ${filterMode === 'baby' ? 'bg-orange-100 dark:bg-orange-900/40' : 'hover:bg-orange-50 dark:hover:bg-orange-900/20'} ${!hasAllergenRisk ? 'rounded-lg' : 'rounded-t-lg border-b border-gray-100 dark:border-slate-700'}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            const newMode = filterMode === 'baby' ? 'zone' : 'baby';
                            setFilterMode(newMode);
                            if (newMode === 'baby') {
                              setActiveZone('Fridge');
                              setActiveSubZone('Tümü');
                              document.getElementById('inventory-list-header')?.scrollIntoView({ behavior: 'smooth' });
                            }
                          }}
                        >
                          <span className="text-[10px] font-bold text-orange-600 dark:text-orange-400">BEBEK</span>
                          <div className="flex items-center gap-1 text-orange-600 dark:text-orange-400">
                            <span className="material-symbols-outlined text-[16px]">child_care</span>
                            <span className="text-sm font-bold">{babyCount}</span>
                          </div>
                        </div>
                      )}

                      {/* Bottom Half: Allergen Risk (if exists) */}
                      {hasAllergenRisk && (
                        <div
                          className={`flex-1 flex items-center justify-center gap-2 cursor-pointer transition-colors ${filterMode === 'allergen' ? 'bg-red-100 dark:bg-red-900/40' : 'hover:bg-red-50 dark:hover:bg-red-900/20'} ${!hasBabyRisk ? 'rounded-lg' : 'rounded-b-lg'}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            const newMode = filterMode === 'allergen' ? 'zone' : 'allergen';
                            setFilterMode(newMode);
                            if (newMode === 'allergen') {
                              setActiveZone('Fridge');
                              setActiveSubZone('Tümü');
                              document.getElementById('inventory-list-header')?.scrollIntoView({ behavior: 'smooth' });
                            }
                          }}
                        >
                          <span className="text-[10px] font-bold text-red-600 dark:text-red-400">ALERJEN</span>
                          <div className="flex items-center gap-1 text-red-600 dark:text-red-400">
                            <span className="material-symbols-outlined text-[16px]">dangerous</span>
                            <span className="text-sm font-bold">{allergenCount}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* 3. Expiring Soon - CLICKABLE */}
                <div
                  className={`flex flex-col items-center justify-center p-2 text-center border-r border-gray-100 dark:border-slate-700 last:border-0 cursor-pointer transition-all hover:bg-red-50 dark:hover:bg-red-900/10 rounded-lg relative ${filterMode === 'expiring' ? 'bg-red-50 dark:bg-red-900/20 ring-1 ring-red-200' : ''}`}
                  onClick={() => {
                    const newMode = filterMode === 'expiring' ? 'zone' : 'expiring';
                    setFilterMode(newMode);
                    if (newMode === 'expiring') {
                      setActiveZone('Fridge');
                      setActiveSubZone('Tümü');
                      setTimeout(() => {
                        document.getElementById('inventory-list-header')?.scrollIntoView({ behavior: 'smooth' });
                      }, 100);
                    }
                  }}
                >
                  {globalStats.expiringCount > 0 && (
                    <div className="absolute top-1 right-1 flex items-center justify-center size-5 bg-red-500 rounded-full text-white shadow-md border-2 border-white dark:border-slate-800 animate-bounce-subtle z-10">
                      <span className="text-[12px] font-black">!</span>
                    </div>
                  )}
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className="text-xs text-gray-500 dark:text-gray-400 font-semibold uppercase leading-none">Skt Kritik</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-red-600 dark:text-red-500">
                    <span className="material-symbols-outlined text-[20px]">history_toggle_off</span>
                    <span className="text-xl font-bold tracking-tight">{globalStats.expiringCount}</span>
                  </div>
                </div>

                {/* 4. Total Value - CLICKABLE */}
                <div
                  className="flex flex-col items-center justify-center p-2 text-center border-r border-gray-100 dark:border-slate-700 last:border-0 cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/10 rounded-lg transition-all relative"
                  onClick={() => setIsValueModalOpen(true)}
                  title="Toplam mutfak değerini ve dökümünü gör"
                >
                  <div className="absolute top-1 right-1 flex items-center justify-center size-5 bg-blue-500 rounded-full text-white shadow-md border-2 border-white dark:border-slate-800 animate-bounce-subtle z-10">
                    <span className="text-[12px] font-black">!</span>
                  </div>
                  <span className="text-xs text-gray-500 dark:text-gray-400 font-semibold mb-1">TOPLAM DEĞER</span>
                  <div className="flex items-center gap-1.5 text-blue-600 dark:text-blue-400">
                    <span className="material-symbols-outlined text-[20px]">payments</span>
                    <span className="text-xl font-bold tracking-tight">{formatCurrency(globalStats.totalValue)}</span>
                  </div>
                </div>

                {/* 5. Rescued Value - CLICKABLE */}
                <div
                  className={`flex flex-col items-center justify-center p-2 text-center border-r border-gray-100 dark:border-slate-700 last:border-0 cursor-pointer transition-all hover:bg-green-50 dark:hover:bg-green-900/10 rounded-lg relative ${filterMode === 'rescued' ? 'bg-green-50 dark:bg-green-900/20 ring-1 ring-green-200' : ''}`}
                  onClick={() => {
                    const newMode = filterMode === 'rescued' ? 'zone' : 'rescued';
                    setFilterMode(newMode);
                    if (newMode === 'rescued') {
                      setActiveZone('Fridge');
                      setActiveSubZone('Tümü');
                      setTimeout(() => {
                        document.getElementById('inventory-list-header')?.scrollIntoView({ behavior: 'smooth' });
                      }, 100);
                    }
                  }}
                  title="Kurtarılan ürünleri listele"
                >
                  <div className="absolute top-1 right-1 flex items-center justify-center size-5 bg-green-500 rounded-full text-white shadow-md border-2 border-white dark:border-slate-800 transition-transform group-hover:scale-110 z-10">
                    <span className="text-[12px] font-black">!</span>
                  </div>
                  <span className="text-xs text-gray-500 dark:text-gray-400 font-semibold mb-1 uppercase">Kurtarılan</span>
                  <div className="flex items-center gap-1.5 text-green-600 dark:text-green-500">
                    <span className="text-[20px] leading-none">🤗</span>
                    <span className="text-xl font-bold tracking-tight">{formatCurrency(globalStats.rescuedValue)}</span>
                  </div>
                </div>

                {/* 6. Wasted Value - CLICKABLE */}
                <div
                  className="group flex flex-col items-center justify-center p-2 text-center last:border-0 cursor-pointer hover:bg-red-50 dark:hover:bg-red-900/10 rounded-lg transition-all border border-transparent hover:border-red-100 dark:hover:border-red-900/30 relative"
                  onClick={openWasteHistory}
                >
                  <div className="absolute top-1 right-1 flex items-center justify-center size-5 bg-red-600 rounded-full text-white shadow-md border-2 border-white dark:border-slate-800 transition-transform group-hover:scale-110 z-10">
                    <span className="text-[12px] font-black">!</span>
                  </div>
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className="text-xs text-gray-500 dark:text-gray-400 font-semibold uppercase">İsraf Olan</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-red-600 dark:text-red-400">
                    <span className="material-symbols-outlined text-[20px]">delete_forever</span>
                    <span className="text-xl font-bold tracking-tight">-{formatCurrency(wastedStats.totalWasted)}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-end justify-between w-full">
              <div className="flex items-center gap-5 overflow-x-auto no-scrollbar -mb-[1px]">
                {zones[activeZone]?.subZones.map((subZone) => (
                  <button
                    key={subZone}
                    onClick={() => {
                      console.log('🔄 SubZone clicked:', { activeZone, oldSubZone: activeSubZone, newSubZone: subZone });
                      setActiveSubZone(subZone);
                    }}
                    className={`pb-3 border-b-[3px] font-semibold text-sm whitespace-nowrap px-1 transition-colors ${activeSubZone === subZone
                      ? 'border-red-600 text-red-600 dark:text-red-400 font-bold'
                      : 'border-transparent text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white'
                      }`}
                  >
                    {subZone}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2 pb-2">
                <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-inner">
                  <button
                    type="button"
                    onClick={handleDirectSearchToSmartSearch}
                    className="text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
                    aria-label="Smart Search aç"
                    title="Smart Search aç"
                  >
                    <span className="material-symbols-outlined text-[18px]">search</span>
                  </button>
                  <input
                    className="bg-transparent text-sm text-gray-800 dark:text-white placeholder-gray-400 text-[12px] focus:outline-none min-w-[180px]"
                    placeholder="Ürün veya marka ara"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleDirectSearchToSmartSearch();
                      }
                    }}
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      className="text-slate-500 hover:text-slate-700 transition-colors"
                      aria-label="Aramayı temizle"
                    >
                      <span className="material-symbols-outlined text-[18px]">close</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </header>
          <div id="inventory-list-header" className="h-0" />

          {/* Content - Natural Scroll */}
          <div className="flex-1 p-4 sm:p-6 relative">
            <div className="max-w-[1200px] mx-auto w-full flex flex-col gap-4 sm:gap-5">

              {/* Breadcrumb */}
              <nav className="flex items-center text-xs font-medium text-gray-500 dark:text-slate-400 overflow-x-auto no-scrollbar">
                <span className="hover:text-red-600 dark:hover:text-red-400 hover:underline underline-offset-4 transition-all cursor-pointer whitespace-nowrap">Mutfağım</span>
                <span className="material-symbols-outlined text-[14px] mx-1.5 text-slate-400 dark:text-slate-600 flex-shrink-0">chevron_right</span>
                <span className="hover:text-red-600 dark:hover:text-red-400 hover:underline underline-offset-4 transition-all cursor-pointer whitespace-nowrap">{zones[activeZone]?.name}</span>
                <span className="material-symbols-outlined text-[14px] mx-1.5 text-slate-400 dark:text-slate-600 flex-shrink-0">chevron_right</span>
                <span className="text-gray-900 dark:text-white font-bold bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 px-2 py-0.5 rounded shadow-sm whitespace-nowrap">{activeSubZone}</span>
              </nav>

          {/* Onboarding Smart Kitchen Banner Desktop */}
          {allInventoryData.some(item => item.originType === 'ONBOARDING_SEED') && (
            <div className="mt-6 bg-gradient-to-r from-primary/10 to-transparent border border-primary/20 rounded-2xl p-4 flex items-center gap-4 relative overflow-hidden">
               <div className="absolute top-0 left-0 w-32 h-32 bg-primary/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2 pointer-events-none"></div>
               <div className="w-12 h-12 rounded-2xl bg-primary/20 flex items-center justify-center shrink-0">
                 <i className="fa-solid fa-wand-magic-sparkles text-primary text-xl"></i>
               </div>
               <div>
                 <h4 className="text-base font-bold text-gray-900 dark:text-white mb-1">Akıllı Mutfağınız Hazır ve Aktif</h4>
                 <p className="text-sm text-gray-500 dark:text-gray-400">
                   Seçtiğiniz rutine göre (Öğrenci, Çalışan vb.) sizin için belirlediğimiz başlangıç malzemeleri listeleniyor. Bu listeyi özgürce düzenleyebilirsiniz.
                 </p>
               </div>
            </div>
          )}

              {/* Desktop Summary Stats */}
              <div className="hidden sm:grid grid-cols-4 gap-4">
                {[
                  { label: 'Toplam', value: filteredInventory.length, icon: 'inventory_2', color: 'blue' },
                  { label: 'Açık Paketler', value: openedCount, icon: 'package_2', color: 'orange' },
                  { label: 'Toplam Tutar', value: formatCurrency(totalPurchasePrice), icon: 'payments', color: 'emerald' },
                  { label: 'Yakında SKT', value: expiringSoonCount, icon: 'history', color: 'red' }
                ].map((stat, idx) => (
                  <div key={idx} className="bg-white dark:bg-slate-900 rounded-xl p-4 border border-gray-200 dark:border-slate-800 shadow-sm flex items-center gap-4 hover:shadow-md transition-shadow">
                    <div className={`p-3 rounded-xl bg-${stat.color}-50 dark:bg-${stat.color}-900/20 text-${stat.color}-600 dark:text-${stat.color}-400`}>
                      <span className="material-symbols-outlined text-[24px]">{stat.icon}</span>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{stat.label}</p>
                      <p className="text-xl font-bold text-gray-900 dark:text-white mt-0.5">{stat.value}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Weekly Review Queue (Manual Item Consumption)
                  [AÇILDI 2026-07-05 — FAZ 2.4 tamam] Pipeline onarıldı:
                  (a) 1.5: ON CONFLICT `WHERE idempotency_key IS NOT NULL` → 42P10 giderildi;
                  (b) 2.4: ConsumptionCandidateScanService (günlük cron) SKT-riski kalemler için
                      MANUAL_ITEM_CANDIDATE üretir → IntelligenceService → pending_review.
                  Panel yalnız bekleyen aday VARKEN görünür (aksi halde boş-panel gürültüsü yok). */}
              {reviewQueue.length > 0 && (
              <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-800 shadow-sm p-4">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div>
                    <h3 className="text-sm font-black text-gray-900 dark:text-white tracking-wide">Haftalık Tüketim Review</h3>
                    <p className="text-xs text-gray-500 dark:text-slate-400">Tarif dışı tüketimleri toplu onaylayın.</p>
                    {reviewTrigger && (
                      <p className="text-[11px] text-slate-500 mt-1">
                        Tetik: {reviewTrigger.reasons?.scheduled ? 'scheduled ' : ''}
                        {reviewTrigger.reasons?.thresholdBased ? 'threshold ' : ''}
                        {reviewTrigger.reasons?.expiryRiskBased ? 'expiry-risk ' : ''}
                        {reviewTrigger.shouldPrompt ? '(önerilen)' : ''}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={fetchConsumptionReviewQueue}
                    className="text-xs px-2.5 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700"
                  >
                    Yenile
                  </button>
                </div>

                {reviewLoading ? (
                  <div className="text-xs text-slate-500">Yükleniyor...</div>
                ) : reviewQueue.length === 0 ? (
                  <div className="text-xs text-slate-500">Review kuyruğunda bekleyen tüketim yok.</div>
                ) : (
                  <div className="space-y-2">
                    {reviewQueue.slice(0, 6).map((q) => (
                      <div key={q.id} className="flex items-center justify-between gap-3 p-2.5 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
                        <div className="min-w-0">
                          <div className="text-sm font-bold text-gray-900 dark:text-white truncate">
                            {q?.metadata?.ingredientName || `Ürün #${q.inventoryItemId}`}
                          </div>
                          <div className="text-xs text-slate-500 flex flex-wrap items-center gap-2">
                            <span>{formatDecimal(q.requestedAmount)} {q.requestedUnit}</span>
                            {Number.isFinite(Number(q?.favoriteScore)) ? (
                              <span className="inline-flex items-center rounded-full border border-indigo-300 dark:border-indigo-800 px-2 py-0.5 text-[10px] font-semibold text-indigo-700 dark:text-indigo-300">
                                Fav skor: {Number(q.favoriteScore).toFixed(1)}
                              </span>
                            ) : null}
                            {q?.autoApplySuggestion?.suggested ? (
                              <span className="inline-flex items-center rounded-full border border-emerald-300 dark:border-emerald-800 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 dark:text-emerald-300">
                                Auto onerilir
                              </span>
                            ) : null}
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <button
                            onClick={() => handleSetAutoApply(q)}
                            className={`text-xs px-2 py-1 rounded border hover:bg-indigo-50 dark:hover:bg-indigo-900/20 ${
                              q?.autoApplySuggestion?.suggested
                                ? 'border-emerald-300 dark:border-emerald-800 text-emerald-700 dark:text-emerald-400'
                                : 'border-indigo-300 dark:border-indigo-800 text-indigo-700 dark:text-indigo-400'
                            }`}
                          >
                            Auto
                          </button>
                          <button
                            onClick={() => handleReviewApprove(q)}
                            className="text-xs px-2 py-1 rounded border border-emerald-300 dark:border-emerald-800 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
                          >
                            Onayla
                          </button>
                          <button
                            onClick={() => handleReviewReject(q)}
                            className="text-xs px-2 py-1 rounded border border-rose-300 dark:border-rose-800 text-rose-700 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/20"
                          >
                            Reddet
                          </button>
                          <button
                            onClick={() => handleMuteSuggestion(q)}
                            className="text-xs px-2 py-1 rounded border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700"
                          >
                            Mute
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              )}

              {/* Products Table/Cards */}
              <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-800 shadow-sm overflow-hidden flex flex-col">
                {loading ? (
                  <div className="flex items-center justify-center p-12">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto"></div>
                      <p className="mt-2 text-sm text-gray-500">Yükleniyor...</p>
                    </div>
                  </div>
                ) : error ? (
                  <div className="flex items-center justify-center p-12">
                    <div className="text-center">
                      <span className="material-symbols-outlined text-[48px] text-gray-300 block mb-2">error</span>
                      <p className="text-sm text-gray-500">{error}</p>
                    </div>
                  </div>
                ) : filteredInventory.length === 0 ? (
                  <div className="flex items-center justify-center p-12">
                    <div className="text-center">
                      <span className="material-symbols-outlined text-[48px] text-gray-300 block mb-2">inventory</span>
                      <p className="text-sm text-gray-500">Bu bölümde henüz malzeme yok</p>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Desktop Table View */}
                    <div className="hidden sm:block">
                      <table className="w-full text-left border-collapse">
                        <thead className="bg-slate-50 dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700">
                          <tr>
                            <th className="px-5 py-3 text-[11px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">Malzeme Adı</th>
                            <th className="px-5 py-3 text-[11px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">Marka</th>
                            {showStatusColumn && <th className="px-5 py-3 text-[11px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">Durum</th>}
                            <th className="px-5 py-3 text-[11px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">SKT Durumu</th>
                            <th className="px-5 py-3 text-[11px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">Miktar / Birim</th>
                            {showPriceColumn && <th className="px-5 py-3 text-[11px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">Fiyat Bilgisi</th>}
                            <th className="px-5 py-3 text-[11px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider text-right">İşlemler</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-slate-800">
                          {filteredInventory.map((item) => {
                            const expiryStatus = getExpiryStatus(item.expiry_date);
                            const babySafetyBadge = getBabySafetyBadge(item);
                            const allergenBadge = getAllergenBadge(item);
                            const lowerItemName = (item.ingredient_name || item.ingredient?.name || '').toLowerCase();

                            // Check for risks for ROW highlighting
                            const userAllergies = [
                              ...(userProfile?.allergies || []),
                              ...(userProfile?.dietaryRestrictions || []),
                              ...(userProfile?.intakeSurvey?.healthConditions || [])
                            ].map(a => a.toLowerCase());

                            const nutrition = item.ingredient?.nutrition_data || {};
                            const allergens = Array.isArray(nutrition.allergens) ? nutrition.allergens : (nutrition.allergen_group && nutrition.allergen_group !== 'none' ? [nutrition.allergen_group] : []);

                            const isAllergenRowRisky = allergens.some(groupKey => {
                              if (groupKey === 'none') return false;
                              const allergenName = groupKey; // Map if needed
                              return userAllergies.some(a =>
                                a === groupKey.toLowerCase() ||
                                (groupKey === 'gluten' && a.includes('gluten')) ||
                                (groupKey === 'nuts' && (a.includes('fındık') || a.includes('fıstık'))) ||
                                (groupKey === 'dairy' && (a.includes('süt') || a.includes('laktor')))
                              );
                            }) || (lowerItemName.includes('bal') && !lowerItemName.includes('balık') && !lowerItemName.includes('kabak') && userAllergies.some(a => a.includes('bal')));

                            const isBabyRowRisky = checkIsBabyRisky(item);

                            const brandRisksRaw = item.brand_allergen_risks || item.brandAllergenRisks || [];
                            const brandRisks = Array.isArray(brandRisksRaw)
                              ? brandRisksRaw
                              : (typeof brandRisksRaw === 'string' ? JSON.parse(brandRisksRaw) : []);

                            const isBrandRisky = brandRisks.some(risk =>
                              userAllergies.some(a =>
                                a.toLowerCase() === risk.toLowerCase() ||
                                (risk.toLowerCase() === 'gluten' && a.toLowerCase().includes('gluten'))
                              )
                            );

                            return (
                              <tr
                                key={item.id}
                                className={`group transition-colors border-b dark:border-slate-800
                                   ${(isAllergenRowRisky || isBrandRisky || isBabyRowRisky)
                                    ? 'bg-red-50 dark:bg-red-900/20 border-l-4 border-l-red-600'
                                    : 'hover:bg-gray-50 dark:hover:bg-gray-800/30'}`}
                              >
                                <td className="px-5 py-3.5">
                                  <div className="font-bold text-gray-900 dark:text-white text-sm flex items-center gap-2">
                                    {isBrandRisky && (
                                      <span
                                        className="material-symbols-outlined text-red-600 animate-pulse fill-1 cursor-help"
                                        title={`⚠️ MARKA RİSKİ: ${brandRisks.join(', ')}`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          Swal.fire({
                                            title: 'Marka Alerjen Riski!',
                                            text: `Bu markada şu riskler tespit edildi: ${brandRisks.join(', ')}. Şefim; daha fazla güvenlik ve alerjan dostu yemek tarifleri için premium satın alabilirsiniz.`,
                                            icon: 'warning',
                                            confirmButtonText: 'Anladım',
                                            background: isDarkMode ? '#1e293b' : '#fff',
                                            color: isDarkMode ? '#e2e8f0' : '#1f2937'
                                          });
                                        }}
                                      >
                                        flag
                                      </span>
                                    )}
                                    {item.ingredient_name || 'Unknown Item'}
                                    <div className="flex items-center gap-1 ml-2">
                                      {babySafetyBadge}
                                      {allergenBadge}
                                    </div>
                                    {(isAllergenRowRisky || isBrandRisky) && <span className="ml-2 text-[10px] text-red-600 font-extrabold tracking-wider animate-pulse uppercase">(!RİSK)</span>}
                                  </div>
                                </td>
                                <td className="px-5 py-3.5">
                                  <span className={`text-[11px] font-black uppercase tracking-wider px-2.5 py-1 rounded-lg border ${item.brand ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400 border-indigo-200 dark:border-indigo-800/50' : 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800/50'}`}>
                                      {item.brand || 'Akıllı Stok'}
                                  </span>
                                </td>

                                {showStatusColumn && (
                                  <td className="px-5 py-3.5">
                                    <div className="flex items-center gap-2">
                                      {(item.can_be_opened || item.is_opened) && (
                                        item.is_opened ? (
                                          <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-orange-50 dark:bg-orange-950/20 text-orange-600 dark:text-orange-400 border border-orange-100/50 dark:border-orange-900/30">
                                            <span className="material-symbols-outlined text-[12px] fill-1">package_2</span>
                                            <span className="text-[10px] font-bold">Açık</span>
                                          </div>
                                        ) : (
                                          <>
                                            <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-green-50 dark:bg-green-950/20 text-green-600 dark:text-green-400 border border-green-100/50 dark:border-green-900/30">
                                              <span className="material-symbols-outlined text-[12px] fill-1">inventory_2</span>
                                              <span className="text-[10px] font-bold">Kapalı</span>
                                            </div>
                                            <button
                                              onClick={() => confirmOpenItem(item)}
                                              title={item.open_action_reason || 'Bu ürünü açtım olarak işaretle'}
                                              className="text-xs px-2 py-1 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-800/40 text-blue-600 dark:text-blue-400 rounded border border-blue-200 dark:border-blue-900 transition-colors"
                                            >
                                              Açıldı
                                            </button>
                                          </>
                                        )
                                      )}
                                    </div>
                                  </td>
                                )}
                                <td className="px-5 py-3.5">
                                  <div
                                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-${expiryStatus.color}-50 dark:bg-${expiryStatus.color}-950/20 text-${expiryStatus.color}-600 dark:text-${expiryStatus.color}-400 border border-${expiryStatus.color}-100/50 dark:border-${expiryStatus.color}-900/30 cursor-help`}
                                    title={expiryStatus.tooltip}
                                  >
                                    <span className="material-symbols-outlined text-[14px] fill-1">{expiryStatus.icon}</span>
                                    <span className="text-[11px] font-bold">{expiryStatus.text}</span>
                                  </div>
                                </td>
                                <td className="px-5 py-3.5">
                                  <div className="flex items-center gap-2 max-w-[100px]">
                                    <div className="flex flex-col">
                                      <span className="font-semibold text-gray-900 dark:text-white text-sm">
                                        {formatDecimal(item.display_quantity ?? item.remaining_amount)}
                                      </span>
                                      {item.display_approx_label && (
                                        <span className="text-[10px] text-slate-500 dark:text-slate-400 leading-tight">
                                          {item.display_approx_label}
                                        </span>
                                      )}
                                    </div>
                                    <select
                                      className="block w-full rounded border-0 py-0.5 pl-1.5 pr-6 text-xs text-gray-500 ring-1 ring-inset ring-slate-200 focus:ring-2 focus:ring-red-600 bg-transparent"
                                      defaultValue={item.display_unit || item.unit}
                                    >
                                      <option value={item.display_unit || item.unit}>{item.display_unit || item.unit}</option>
                                      <option>kg</option>
                                      <option>gr</option>
                                      <option>pcs</option>
                                      <option>lt</option>
                                      <option>ml</option>
                                    </select>
                                  </div>
                                </td>
                                {showPriceColumn && (
                                  <td className="px-5 py-3.5">
                                    {(() => {
                                      const priceVal = getBackendLineTotal(item);
                                      if (item.is_rescued === true || (!isNaN(priceVal) && priceVal < 0)) {
                                        return (
                                          <div className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800">
                                            <span className="font-black text-xs">+{formatCurrency(Math.abs(priceVal || 0))}</span>
                                            <span className="text-[14px] leading-none">👏</span>
                                          </div>
                                        );
                                      }
                                      return (
                                        <span className="text-sm font-semibold text-gray-900 dark:text-white">
                                          {item.priceStatus === 'invalid' ? '-' : (priceVal ? formatCurrency(priceVal) : '-')}
                                        </span>
                                      );
                                    })()}
                                  </td>
                                )}
                                <td className="px-5 py-3.5 text-right">
                                  <div className="flex items-center justify-end gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                                    <button
                                      className={`p-1.5 rounded transition-colors ${
                                        isInventoryFavorite(item)
                                          ? 'bg-rose-50 dark:bg-rose-950/20 text-rose-600 dark:text-rose-300 hover:bg-rose-100 dark:hover:bg-rose-900/30'
                                          : 'hover:bg-rose-50 dark:hover:bg-rose-950/20 text-slate-500 dark:text-slate-400 hover:text-rose-600 dark:hover:text-rose-300'
                                      }`}
                                      title={isInventoryFavorite(item) ? 'Favoriden çıkar' : 'Favoriye ekle'}
                                      disabled={favoriteBusyEntityId === getInventoryFavoriteEntityId(item)}
                                      onClick={() => toggleInventoryFavorite(item)}
                                    >
                                      <span className="material-symbols-outlined text-[18px]" style={{ fontVariationSettings: isInventoryFavorite(item) ? "'FILL' 1" : "'FILL' 0" }}>favorite</span>
                                    </button>
                                    <button
                                      className="p-1.5 rounded hover:bg-emerald-50 dark:hover:bg-emerald-950/20 text-slate-500 dark:text-slate-400 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors"
                                      title="Bunu yedim"
                                      onClick={() => handleQuickManualConsume(item)}
                                    >
                                      <span className="material-symbols-outlined text-[18px]">restaurant</span>
                                    </button>
                                    <button
                                      className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                                      title="Düzenle"
                                      onClick={() => openEditModal(item)}
                                    >
                                      <span className="material-symbols-outlined text-[18px]">edit</span>
                                    </button>
                                    <button
                                      className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-950/20 text-slate-500 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                                      title="Sil"
                                      onClick={() => openDeleteModal(item)}
                                    >
                                      <span className="material-symbols-outlined text-[18px]">delete</span>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>


                  </>
                )}
              </div>
            </div>


          </div>
        </div>
      </div >
      {/* Edit Modal */}
      {
        editModalOpen && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[120] p-4 transition-all animate-in fade-in">
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-white/10 dark:border-white/5">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 dark:border-slate-800">
                <div>
                  <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white tracking-tight">
                    {modalMode === 'edit' ? 'Ürün Düzenle' : 'Yeni Ürün Ekle'}
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 font-medium">
                    {modalMode === 'edit'
                      ? `${editingItem?.ingredient_name || editingItem?.ingredient?.name} bilgilerini güncelleyin`
                      : `${activeSubZone} bölümüne yeni ürün ekleyin`
                    }
                  </p>
                </div>
                <button
                  onClick={closeEditModal}
                  className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-slate-800 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-white transition-all active:scale-95"
                >
                  <span className="material-symbols-outlined text-[24px]">close</span>
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
                {/* Ürün Adı */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="relative">
                    <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
                      Ürün Adı
                    </label>
                    <input
                      type="text"
                      value={editForm.ingredient_name}
                      onChange={(e) => handleEditFormChange('ingredient_name', e.target.value)}
                      className="w-full px-4 py-2.5 bg-gray-50 dark:bg-slate-800 border border-gray-300 dark:border-slate-700 text-gray-900 dark:text-white rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all font-medium"
                      placeholder="Ürün Adı"
                    />
                    {/* Ontology Search Results Popup - Only show if NO family chips are present to avoid redundancy */}
                    {ontologyResults.length > 0 && !getIngredientFamily(editForm.ingredient_name) && (
                      <div className="absolute top-full left-0 right-0 z-[150] bg-white dark:bg-slate-900 mt-1 rounded-xl shadow-2xl border border-gray-100 dark:border-slate-800 overflow-hidden max-h-60 overflow-y-auto">
                        {ontologyResults.map((item, idx) => (
                          <button
                            key={idx}
                            onClick={() => selectFromOntology(item)}
                            className="w-full flex items-center justify-between p-3 hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors border-b border-gray-50 dark:border-slate-800 last:border-0 text-left group"
                          >
                            <div className="flex items-center gap-3">
                              <div className="size-8 rounded-lg bg-gray-50 dark:bg-slate-800 flex items-center justify-center text-primary">
                                <span className="material-symbols-outlined text-[20px]">nutrition</span>
                              </div>
                              <div>
                                <div className="text-xs font-bold text-gray-900 dark:text-white">{item.name}</div>
                                <div className="text-[10px] text-gray-400 uppercase tracking-widest">{item.family}</div>
                              </div>
                            </div>
                            <span className="material-symbols-outlined text-gray-300 group-hover:text-primary text-[18px]">add_circle</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
                      Marka
                    </label>
                    <input
                      type="text"
                      value={editForm.brand || ''}
                      onChange={(e) => handleEditFormChange('brand', e.target.value)}
                      className="w-full px-4 py-2.5 bg-gray-50 dark:bg-slate-800 border border-gray-300 dark:border-slate-700 text-gray-900 dark:text-white rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all font-medium"
                      placeholder="Marka (Örn: Sütaş)"
                    />
                  </div>
                </div>

                {/* Pasteurization Check (For Dairy) */}
                {isDairyProduct(editForm.ingredient_name) && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-xl border border-blue-100 dark:border-blue-800 animate-fade-in-up">
                    <label className="flex items-center gap-3 cursor-pointer group">
                      <div className="relative flex items-center">
                        <input
                          type="checkbox"
                          checked={editForm.is_pasteurized}
                          onChange={(e) => handleEditFormChange('is_pasteurized', e.target.checked)}
                          className="peer h-6 w-6 cursor-pointer appearance-none rounded-md border border-blue-300 dark:border-blue-700 bg-white dark:bg-slate-800 checked:bg-blue-600 checked:border-blue-600 transition-all"
                        />
                        <span className="material-symbols-outlined absolute opacity-0 peer-checked:opacity-100 text-white pointer-events-none text-xl left-0 top-0 h-6 w-6 flex items-center justify-center">
                          check
                        </span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm font-bold text-blue-900 dark:text-blue-300">Pastörize Ürün</span>
                        <span className="text-[11px] text-blue-700 dark:text-blue-400">Genellikle market ürünleri pastörizedir. Bebeğiniz için daha güvenlidir.</span>
                      </div>
                    </label>
                  </div>
                )}

                {/* LEGUME TYPE SELECTOR (Dynamic) */}
                {(() => {
                  const name = (editForm.ingredient_name || '').toLowerCase();
                  const isLegumeMatch = name.match(/fasulye|nohut|mercimek|barbunya|börülce/i);
                  const isTaze = name.includes('taze');
                  return isLegumeMatch && !isTaze;
                })() && (
                    <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-xl border border-orange-100 dark:border-orange-800 animate-fade-in-up">
                      <label className="block text-sm font-bold text-orange-800 dark:text-orange-300 mb-3 flex items-center gap-2">
                        <span className="material-symbols-outlined">restaurant_menu</span>
                        Bakliyat Tipi
                      </label>
                      <div className="flex gap-4">
                        <button
                          type="button"
                          onClick={() => handleBakliyatTypeChange('raw')}
                          className={`flex-1 py-2 px-3 rounded-lg border text-xs font-bold transition-all flex items-center justify-center gap-2
                      ${!editForm.ingredient_name.includes('(Haşlanmış)') && !editForm.ingredient_name.includes('Konserve')
                              ? 'bg-orange-500 text-white border-orange-600 shadow-md'
                              : 'bg-white dark:bg-slate-800 text-slate-500 border-slate-200 dark:border-slate-700'}`}
                        >
                          <span className="material-symbols-outlined text-[16px]">grain</span>
                          Kuru / Çiğ
                        </button>
                        <button
                          type="button"
                          onClick={() => handleBakliyatTypeChange('cooked')}
                          className={`flex-1 py-2 px-3 rounded-lg border text-xs font-bold transition-all flex items-center justify-center gap-2
                      ${editForm.ingredient_name.includes('(Haşlanmış)') || editForm.ingredient_name.includes('Konserve')
                              ? 'bg-green-500 text-white border-green-600 shadow-md'
                              : 'bg-white dark:bg-slate-800 text-slate-500 border-slate-200 dark:border-slate-700'}`}
                        >
                          <span className="material-symbols-outlined text-[16px]">soup_kitchen</span>
                          Haşlanmış / Konserve
                        </button>
                      </div>
                    </div>
                  )}

                {/* 🌟 FAMILY VARIANT SELECTOR */}
                {(() => {
                  const rawName = editForm.ingredient_name || '';
                  // Basit bir lookup yapalım, import sorunu yaşamamak için inline logic veya üstte tanımlı olmalı.
                  // Import'u yukarı ekleyeceğiz, burada kullanacağız.
                  const family = getIngredientFamily(rawName);

                  if (family && family.variants) {
                    return (
                      <div className="mb-4 animate-fade-in-down">
                        <label className="block text-xs font-bold text-purple-500 uppercase tracking-wider mb-2">
                          Hangi {family.default ? `"${family.default.split(' ')[0]}"` : 'Tür'}?
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {family.variants.map(variant => (
                            <button
                              key={variant}
                              type="button"
                              onClick={() => handleEditFormChange('ingredient_name', variant)}
                              className="px-3 py-1.5 text-xs font-bold rounded-lg border border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 hover:bg-purple-100 dark:hover:bg-purple-900/40 transition-colors"
                            >
                              {variant}
                            </button>
                          ))}
                          {/* Default'a dönme butonu */}
                          <button
                            type="button"
                            onClick={() => handleEditFormChange('ingredient_name', family.default)}
                            className="px-3 py-1.5 text-xs font-bold rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-500 hover:bg-gray-50 transition-colors"
                          >
                            {family.default} (Varsayılan)
                          </button>
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}

                {/* Miktar ve Birim */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
                      Kalan Miktar
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={editForm.remaining_amount}
                      onChange={(e) => handleEditFormChange('remaining_amount', e.target.value)}
                      className="w-full px-4 py-2.5 bg-gray-50 dark:bg-slate-800 border border-gray-300 dark:border-slate-700 text-gray-900 dark:text-white rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all font-medium"
                      placeholder="Miktar"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
                      Birim
                    </label>
                    <select
                      value={editForm.unit}
                      onChange={(e) => handleEditFormChange('unit', e.target.value)}
                      className="w-full px-4 py-2.5 bg-gray-50 dark:bg-slate-800 border border-gray-300 dark:border-slate-700 text-gray-900 dark:text-white rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all font-medium"
                    >
                      <option value="gr">gram (gr)</option>
                      <option value="kg">kilogram (kg)</option>
                      <option value="ml">mililitre (ml)</option>
                      <option value="lt">litre (lt)</option>
                      <option value="adet">adet</option>
                      <option value="paket">paket</option>
                      <option value="kutu">kutu</option>
                    </select>
                  </div>
                </div>

                {/* Son Kullanma Tarihi ve Fiyat */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    {modalMode === 'edit' ? (
                      <>
                        <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
                          Son Kullanma Tarihi
                        </label>
                        <input
                          type="date"
                          value={editForm.expiry_date}
                          onChange={(e) => handleEditFormChange('expiry_date', e.target.value)}
                          className="w-full px-4 py-2.5 bg-gray-50 dark:bg-slate-800 border border-gray-300 dark:border-slate-700 text-gray-900 dark:text-white rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all font-medium"
                        />
                      </>
                    ) : (
                      <div className="flex flex-col gap-1 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-xl">
                        <div className="flex items-center gap-2 text-blue-700 dark:text-blue-400 font-bold text-sm">
                          <span className="material-symbols-outlined text-[18px]">auto_awesome</span>
                          <span>Otomatik SKT</span>
                        </div>
                        <p className="text-xs text-blue-600 dark:text-blue-500">
                          Ürün türüne göre son kullanma tarihi sistem tarafından otomatik hesaplanacaktır.
                        </p>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
                      Alış Fiyatı (₺)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={editForm.purchase_price}
                      onChange={(e) => handleEditFormChange('purchase_price', e.target.value)}
                      className="w-full px-4 py-2.5 bg-gray-50 dark:bg-slate-800 border border-gray-300 dark:border-slate-700 text-gray-900 dark:text-white rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all font-medium"
                      placeholder="Fiyat"
                    />
                  </div>
                </div>

                {/* Depolama Yeri */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
                    Depolama Yeri
                  </label>
                  <input
                    type="text"
                    value={editForm.storage_location}
                    onChange={(e) => handleEditFormChange('storage_location', e.target.value)}
                    className="w-full px-4 py-2.5 bg-gray-50 dark:bg-slate-800 border border-gray-300 dark:border-slate-700 text-gray-900 dark:text-white rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all font-medium"
                    placeholder="Örn: Buzdolabı Alt Raf"
                  />
                </div>

                {/* Zone ve SubZone */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
                      Ana Bölge
                    </label>
                    <select
                      value={editForm.main_zone}
                      onChange={(e) => handleEditFormChange('main_zone', e.target.value)}
                      className="w-full px-4 py-2.5 bg-gray-50 dark:bg-slate-800 border border-gray-300 dark:border-slate-700 text-gray-900 dark:text-white rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all font-medium"
                    >
                      <option value="Fridge">Buzdolabı</option>
                      <option value="Freezer">Dondurucu</option>
                      <option value="Pantry">Kiler</option>
                      <option value="Snack_Cabinet">Atıştırmalık Dolabı</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
                      Alt Bölge
                    </label>
                    <select
                      value={editForm.sub_zone}
                      onChange={(e) => handleEditFormChange('sub_zone', e.target.value)}
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-slate-800 border border-gray-300 dark:border-slate-700 text-gray-900 dark:text-white rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all font-medium"
                    >
                      {editForm.main_zone === 'Fridge' && (
                        <>
                          <option value="Vegetables">Sebzeler</option>
                          <option value="Fruits">Meyveler</option>
                          <option value="Dairy">Süt Ürünleri</option>
                          <option value="Meat_Fish">Et & Balık</option>
                          <option value="Sauces">Soslar</option>
                          <option value="General">Genel</option>
                        </>
                      )}
                      {editForm.main_zone === 'Freezer' && (
                        <>
                          <option value="Frozen_Meat">Donmuş Et</option>
                          <option value="Frozen_Vegetables">Donmuş Sebze</option>
                          <option value="Desserts">Tatlılar</option>
                          <option value="General">Genel</option>
                        </>
                      )}
                      {editForm.main_zone === 'Pantry' && (
                        <>
                          <option value="Grains">Tahıllar</option>
                          <option value="Spices">Baharatlar</option>
                          <option value="Canned">Konserveler</option>
                          <option value="General">Genel</option>
                        </>
                      )}
                      {editForm.main_zone === 'Snack_Cabinet' && (
                        <>
                          <option value="Snacks">Atıştırmalıklar</option>
                          <option value="Beverages">İçecekler</option>
                          <option value="Baking">Fırın Malzemeleri</option>
                          <option value="General">Genel</option>
                        </>
                      )}
                    </select>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="flex items-center justify-end gap-3 p-4 sm:p-6 border-t border-gray-200 dark:border-slate-800">
                <button
                  onClick={closeEditModal}
                  className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white font-bold transition-colors"
                >
                  İptal
                </button>
                <button
                  onClick={saveEditedItem}
                  disabled={loading}
                  className="px-8 py-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl shadow-lg shadow-red-600/20 active:scale-95 transition-all disabled:opacity-50"
                >
                  {loading
                    ? (modalMode === 'edit' ? 'Kaydediliyor...' : 'Ekleniyor...')
                    : (modalMode === 'edit' ? 'Kaydet' : 'Ekle')
                  }
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Delete Confirmation Modal */}
      {
        deleteModalOpen && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[120] p-4 transition-all animate-in fade-in">
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md border border-white/10 dark:border-white/5">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 dark:border-slate-800">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-red-100 dark:bg-red-900/30 rounded-2xl">
                    <span className="material-symbols-outlined text-red-600 dark:text-red-400 text-[24px] fill-1">warning</span>
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-gray-900 dark:text-white tracking-tight">Ürünü Sil</h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Bu işlem geri alınamaz</p>
                  </div>
                </div>
                <button
                  onClick={closeDeleteModal}
                  className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-slate-800 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-white transition-all"
                >
                  <span className="material-symbols-outlined text-[24px]">close</span>
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-4 sm:p-6">
                <div className="text-center">
                  <p className="text-gray-700 dark:text-gray-300 mb-4 font-medium">
                    <strong className="text-gray-900 dark:text-white font-bold">{deletingItem?.ingredient_name || deletingItem?.ingredient?.name}</strong> ürününü silmek istediğinizden emin misiniz?
                  </p>

                  {deletingItem && (() => {
                    const now = new Date();
                    const isExpired = deletingItem.expiry_date && new Date(deletingItem.expiry_date) <= now;
                    const addedAt = deletingItem.purchase_date || deletingItem.created_at || now;
                    const hoursInStock = Math.abs(now - new Date(addedAt)) / 36e5;
                    const isStale = hoursInStock > 24;

                    if (isExpired || isStale) {
                      return (
                        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/30 rounded-2xl flex items-start gap-3 text-left">
                          <span className="material-symbols-outlined text-red-600 dark:text-red-400 shrink-0">warning</span>
                          <div>
                            <p className="text-sm font-bold text-red-800 dark:text-red-300 uppercase">İsraf Bildirimi!</p>
                            <p className="text-[11px] text-red-700 dark:text-red-400 font-medium leading-tight mt-0.5">
                              Bu ürün {isExpired ? 'süresi geçtiği için' : '24 saatten fazla stokta beklediği için'} silindiğinde
                              <strong className="block mt-1 underline">İSRAF HANESİNE EKLEME YAPILACAKTIR.</strong>
                              Onaylıyor musunuz?
                            </p>
                          </div>
                        </div>
                      );
                    }
                    return (
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Bu işlem geri alınamaz ve tüm ürün bilgileri kalıcı olarak silinecektir.
                      </p>
                    );
                  })()}
                </div>
              </div>

              {/* Modal Footer */}
              <div className="flex items-center justify-end gap-3 p-4 sm:p-6 border-t border-gray-200 dark:border-slate-800">
                <button
                  onClick={closeDeleteModal}
                  className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white font-bold transition-colors"
                >
                  İptal
                </button>
                <button
                  onClick={deleteItem}
                  disabled={loading}
                  className="px-8 py-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl shadow-lg shadow-red-600/20 active:scale-95 transition-all disabled:opacity-50"
                >
                  {loading ? 'Siliniyor...' : 'Sil'}
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Baby Modal (shared hook-driven) */}
      <BabyModal
        isOpen={localBabyModalOpen}
        onClose={handleBabyModalClose}
        onSubmit={handleBabyModalSubmit}
      />

      {/* Open Confirmation Modal */}
      <OpenConfirmationModal
        isOpen={!!openConfirmItem}
        onClose={() => setOpenConfirmItem(null)}
        onConfirm={handleOpenConfirm}
        itemName={openConfirmItem?.ingredient_name || openConfirmItem?.ingredient?.name}
      />

      {/* Wasted Items History Modal */}
      {
        isWasteHistoryOpen && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 sm:p-6">
            <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setIsWasteHistoryOpen(false)}></div>
            <div className="relative w-full max-w-2xl bg-white dark:bg-slate-900 rounded-[2.5rem] shadow-2xl overflow-hidden flex flex-col max-h-[90vh] border border-white/10">
              {/* Header */}
              <div className="p-6 border-b border-gray-100 dark:border-slate-800 flex items-center justify-between bg-red-50/30 dark:bg-red-900/10">
                <div className="flex items-center gap-3">
                  <div className="size-12 rounded-2xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center text-red-600 dark:text-red-400">
                    <span className="material-symbols-outlined text-[28px]">delete_forever</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white uppercase tracking-tight">İsraf Geçmişi</h3>
                    <p className="text-[10px] text-red-600 dark:text-red-400 font-black uppercase tracking-widest">Mutfak Kayıp Raporu</p>
                  </div>
                </div>
                <button
                  onClick={() => setIsWasteHistoryOpen(false)}
                  className="size-10 rounded-xl hover:bg-white dark:hover:bg-slate-800 text-gray-400 hover:text-gray-600 dark:hover:text-white transition-all flex items-center justify-center shadow-sm"
                >
                  <span className="material-symbols-outlined">close</span>
                </button>
              </div>

              {/* List */}
              <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-3 no-scrollbar">
                {wasteHistoryLoading ? (
                  <div className="py-12 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto"></div>
                    <p className="mt-2 text-sm text-gray-500">Yükleniyor...</p>
                  </div>
                ) : wasteHistory.length === 0 ? (
                  <div className="py-12 text-center">
                    <p className="text-gray-500 font-medium">Henüz israf kaydı bulunmuyor. Harika! ✨</p>
                  </div>
                ) : (
                  wasteHistory.map((item) => (
                    <div key={item.id} className="group relative flex flex-col p-4 bg-gray-50 dark:bg-slate-800/50 rounded-2xl border border-gray-100 dark:border-slate-800 transition-all hover:border-red-100 dark:hover:border-red-900/30 gap-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="size-10 rounded-xl bg-white dark:bg-slate-800 shadow-sm border border-gray-100 dark:border-slate-700 flex items-center justify-center text-red-500">
                            <span className="material-symbols-outlined text-[20px]">delete_sweep</span>
                          </div>
                          <div>
                            <h4 className="font-bold text-gray-900 dark:text-white text-[14px] leading-tight capitalize">{item.ingredientName}</h4>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase">{item.amount} {item.unit}</span>
                              <span className="size-1 bg-slate-300 dark:bg-slate-700 rounded-full"></span>
                              <span className="text-[11px] text-slate-400 dark:text-slate-500">
                                {new Date(item.wastedAt).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-red-600 dark:text-red-400 font-black text-sm">-{formatCurrency(item.purchasePrice)}</div>
                          <p className="text-[9px] text-slate-400 dark:text-slate-500 font-bold uppercase tracking-tighter">Maliyet</p>
                        </div>
                      </div>

                      {/* Smart Advice Card */}
                      <div className="flex items-start gap-2.5 p-3 bg-white dark:bg-slate-900/50 rounded-xl border border-blue-50 dark:border-blue-900/20 shadow-sm ring-1 ring-blue-50/50">
                        <span className="material-symbols-outlined text-[18px] text-blue-500 mt-0.5">lightbulb</span>
                        <p className="text-[11px] font-medium text-slate-600 dark:text-slate-300 leading-relaxed italic">
                          {generateSavingsAdvice(item)}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Footer Summary */}
              <div className="p-6 bg-slate-50 dark:bg-slate-800/80 border-t border-gray-100 dark:border-slate-800">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[10px] text-slate-500 dark:text-slate-400 font-bold uppercase tracking-widest">Toplam Kayıp</p>
                    <p className="text-2xl font-black text-red-600 dark:text-red-400">-{formatCurrency(wastedStats.totalWasted)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] text-slate-500 dark:text-slate-400 font-bold uppercase tracking-widest">Öğe Sayısı</p>
                    <p className="text-lg font-bold text-gray-900 dark:text-white">{wastedStats.count} Ürün</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* Value Breakdown Modal */}
      {
        isValueModalOpen && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 sm:p-6">
            <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setIsValueModalOpen(false)}></div>
            <div className="relative w-full max-w-md bg-white dark:bg-slate-900 rounded-[2.5rem] shadow-2xl overflow-hidden flex flex-col border border-white/10 animate-in fade-in zoom-in duration-200">
              {/* Header */}
              <div className="p-6 border-b border-gray-100 dark:border-slate-800 flex items-center justify-between bg-blue-50/30 dark:bg-blue-900/10">
                <div className="flex items-center gap-3">
                  <div className="size-12 rounded-2xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400">
                    <span className="material-symbols-outlined text-[28px]">payments</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white uppercase tracking-tight">Değer Analizi</h3>
                    <p className="text-[10px] text-blue-600 dark:text-blue-400 font-black uppercase tracking-widest">Mutfak Ekonomisi</p>
                  </div>
                </div>
                <button
                  onClick={() => setIsValueModalOpen(false)}
                  className="size-10 rounded-xl hover:bg-white dark:hover:bg-slate-800 text-gray-400 hover:text-gray-600 dark:hover:text-white transition-all flex items-center justify-center shadow-sm"
                >
                  <span className="material-symbols-outlined">close</span>
                </button>
              </div>

              {/* Content */}
              <div className="p-6 space-y-4">
                {/* Active Inventory */}
                <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-gray-100 dark:border-slate-700">
                  <div className="flex items-center gap-3">
                    <div className="size-10 rounded-xl bg-white dark:bg-slate-800 flex items-center justify-center text-blue-500 shadow-sm">
                      <span className="material-symbols-outlined text-[20px]">inventory_2</span>
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-500 dark:text-slate-400 font-bold uppercase tracking-widest">Mutfak Malzeme Değeri</p>
                      <p className="text-sm font-bold text-gray-900 dark:text-white">Depodaki Ürünler</p>
                    </div>
                  </div>
                  <div className="text-right text-lg font-black text-blue-600 dark:text-blue-400">
                    {formatCurrency(globalStats.activeInventoryValue)}
                  </div>
                </div>

                {/* Rescued Value */}
                <div className="flex items-center justify-between p-4 bg-green-50/50 dark:bg-green-900/10 rounded-2xl border border-green-100 dark:border-green-900/20">
                  <div className="flex items-center gap-3">
                    <div className="size-10 rounded-xl bg-white dark:bg-slate-800 flex items-center justify-center text-green-500 shadow-sm">
                      <span className="text-[20px] leading-none">🤗</span>
                    </div>
                    <div>
                      <p className="text-[10px] text-green-600 dark:text-green-500 font-bold uppercase tracking-widest">Kurtarılan Ürün Değeri</p>
                      <p className="text-sm font-bold text-gray-900 dark:text-white">Ekonomiye Kazandırılan</p>
                    </div>
                  </div>
                  <div className="text-right text-lg font-black text-green-600 dark:text-green-400">
                    +{formatCurrency(globalStats.rescuedValue)}
                  </div>
                </div>

                {/* Total Divider */}
                <div className="pt-2 border-t border-dashed border-slate-200 dark:border-slate-700">
                  <div className="flex items-center justify-between px-2">
                    <p className="text-sm font-black text-gray-900 dark:text-white uppercase tracking-tighter">Toplam Mutfak Değeri</p>
                    <p className="text-2xl font-black text-blue-600 dark:text-blue-400">
                      {formatCurrency(globalStats.totalValue)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Footer Tip */}
              <div className="p-4 bg-blue-50 dark:bg-blue-900/10 text-center">
                <p className="text-[11px] font-medium text-blue-600 dark:text-blue-300 leading-relaxed italic">
                  {hasChildren
                    ? `“İsraf etmediğin her ürün, ${(babyProfile?.babyName || userProfile?.babyName || 'bebeğinin')} yaşadığı dünyayı daha güzel bir yer yapacak. ✨”`
                    : "“Kurtarılan ürünlerin değeri, toplam bütçene artı olarak eklenmiştir. İsraf etmediğin her gram değerlidir! ✨”"}
                </p>
              </div>
            </div>
          </div>
        )
      }
      {/* Quick Wizard Modal */}
      {
        isWizardOpen && (
          <QuickWizard
            onClose={() => setIsWizardOpen(false)}
            onComplete={() => {
              setIsWizardOpen(false);
              fetchInventoryData(); // Refresh data after wizard
              setTimeout(() => triggerBabyPrompt(), 500); // Trigger baby prompt check
            }}
          />
        )
      }

      {/* Market List Wizard */}
      <MarketListWizard isOpen={isMarketWizardOpen} onClose={() => setIsMarketWizardOpen(false)} />

      {/* Mobile-Only Circular Menu */}
      <CircularMenu
        onAdd={() => openAddModal()}
        onWizard={() => setIsWizardOpen(true)}
        onMarket={() => setIsMarketWizardOpen(true)}
        onSearch={handleInventorySearch}
      />

      {/* Smart Search Results Modal Component */}
      <SmartSearchModal
        isOpen={smartSearchModalOpen}
        onClose={() => setSmartSearchModalOpen(false)}
        loading={smartSearchLoading}
        recipes={smartSearchRecipes}
        structuredMenu={smartStructuredMenu}
        onRecipeClick={handleSmartRecipeClick}
        isDarkMode={isDarkMode}
        selectedRecipe={selectedSmartRecipe}
        recipeModalOpen={smartRecipeModalOpen}
        onRecipeModalClose={() => setSmartRecipeModalOpen(false)}
        userProfile={userProfile}
        onSearchUpdate={(cats, opts) => {
          if (opts?.portion) setSmartPortion(opts.portion);
          if (opts?.skillLevel) setSmartSkillLevel(opts.skillLevel);
          handleInventorySearch(cats, opts);
        }}
        onRecipeUsedSuccess={handleRecipeUsedSuccess}
        onUsed={() => {
          fetchInventoryData();
          fetchAllInventoryData();
          fetchBehaviorDecisionSet();
        }}
        availableComponents={availableComponents}
        initialPortion={smartPortion}
        initialSkillLevel={smartSkillLevel}
      />

      <StapleModal
        isOpen={isStapleModalOpen}
        onClose={() => setIsStapleModalOpen(false)}
        staples={stapleList}
        loading={stapleLoading}
      />

    </div>
  );
}

const StapleModal = ({ isOpen, onClose, staples, loading }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 sm:p-6">
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-md animate-in fade-in duration-300"
        onClick={onClose}
      />
      <div className="relative w-full max-w-lg bg-white dark:bg-slate-900 rounded-3xl shadow-2xl overflow-hidden border border-white/20 animate-in zoom-in-95 duration-300">
        {/* Header */}
        <div className="bg-red-600 p-6 text-white relative">
          <div className="absolute top-4 right-4">
            <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full transition-colors">
              <span className="material-symbols-outlined">close</span>
            </button>
          </div>
          <div className="flex items-center gap-3 mb-2">
            <div className="size-10 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
              <span className="material-symbols-outlined text-2xl">checklist</span>
            </div>
            <h2 className="text-xl font-black tracking-tight uppercase">Varsayılan Listesi</h2>
          </div>
          <p className="text-red-50 text-xs font-medium leading-relaxed opacity-90">
            Şefim; sistem tarif önerebilmek için bazı temel malzemeleri mutfağınızda var kabul eder.
            Bu malzemeler stok takibine dahil değildir.
            Gerçek stok takibi yapmak isterseniz, elinizde bulunan ürünleri eklemeniz gerekir.
          </p>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12 gap-4">
              <div className="size-12 border-4 border-red-100 border-t-red-600 rounded-full animate-spin" />
              <p className="text-sm font-bold text-gray-500 animate-pulse">Liste yükleniyor...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[40vh] overflow-y-auto px-1 custom-scrollbar">
              {staples && staples.length > 0 ? (
                staples.map((staple, idx) => (
                  <div key={idx} className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-slate-800/50 rounded-xl border border-gray-100 dark:border-slate-700 hover:border-red-200 dark:hover:border-red-900/30 transition-colors group">
                    <div className="size-2 rounded-full bg-red-400 group-hover:scale-125 transition-transform" />
                    <span className="text-sm font-bold text-gray-700 dark:text-gray-300 truncate capitalize" title={staple}>{staple}</span>
                  </div>
                ))
              ) : (
                <div className="col-span-2 py-10 text-center">
                  <p className="text-gray-500 text-sm font-medium italic">Henüz varsayılan malzeme tanımlanmamış. 🍽️</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 bg-gray-50 dark:bg-slate-800/50 border-t border-gray-100 dark:border-slate-700 text-center">
          <button
            onClick={onClose}
            className="w-full py-3 bg-gray-900 dark:bg-white text-white dark:text-gray-900 font-black rounded-2xl hover:scale-[1.02] active:scale-95 transition-all shadow-lg"
          >
            ANLADIM
          </button>
        </div>
      </div>
    </div >
  );
};
